import {mxEvent, mxEventObject, mxResources, mxUtils,} from './mxGraphFactory';

import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

import 'fomantic-ui-less/definitions/elements/loader.less';

import 'fomantic-ui-less/definitions/modules/transition.less';
import 'fomantic-ui-less/definitions/modules/tab.less';
import 'fomantic-ui-less/definitions/modules/dropdown.less';
import 'fomantic-ui-less/definitions/modules/search.less';
import 'fomantic-ui-less/definitions/modules/popup.less';
import 'fomantic-ui-less/definitions/modules/calendar.less';
import 'fomantic-ui-less/definitions/modules/checkbox.less';
import 'fomantic-ui-less/definitions/modules/accordion.less';

import 'fomantic-ui-less/definitions/modules/transition';
import 'fomantic-ui-less/definitions/modules/tab';
import 'fomantic-ui-less/definitions/modules/dropdown';
import 'fomantic-ui-less/definitions/modules/search';
import 'fomantic-ui-less/definitions/modules/popup';
import 'fomantic-ui-less/definitions/modules/calendar';
import 'fomantic-ui-less/definitions/modules/checkbox';
import 'fomantic-ui-less/definitions/modules/accordion';

import 'fomantic-ui-less/definitions/behaviors/api';

import {deepCopy} from './helper';
import {api} from '../Common/fetchWithTimeout';
import {stringifyJSON} from '../Common/json';
import {createLogger} from '../Common/logger';
import {fromWorkflowDefinition, getWorkflowStartPage, normalizePageReference, setWorkflowStartPage, vectorTaskImage} from './workflow';
import {EditorUi} from './EditorUi';
import {Editor} from './Editor';
import {dataCopyValue} from '../Common/copyToClipboard';
import {pageVersionsSelectDropdown, setupDropdowns, workflowDefinitionVersionsSelectDropdown} from '../Common/dropdowns';
import {setupCloseableMessages} from '../Common/message';
import {javascriptHint} from '../Common/javaScriptHint';
import {UPDATE_CELL_VALUE} from './Graph';
import {urlData} from '../Common/urlData';
import {urlEncodedValue} from '../Common/urlEncodedValue';
import {aiProviders, getProviderLogo} from "../Common/aiProviders";
import {
    createCheckboxes as createFormCheckboxes,
    createDropdown as createFormDropdown,
    createField as createFormField,
    createFields as createFormFields,
    createInputField as createFormInputField,
    createTable as createFormTable,
    createTextareaField as createFormTextareaField,
    normalizeFieldErrors
} from './forms/formFields';
import {updateCellValue, updateRootValue} from './forms/formState';
import {
    isBlankOrJsonObject,
    isPlainJsonObject,
    stringifyJsonFallbackValue,
    syncJsonFallbackValue
} from './forms/jsonFallbackField';
import {createTestResponseField} from './forms/testResponseFields';
import {initializeCredentialsDropdown} from './forms/credentialsDropdown';
import {v4 as uuid} from 'uuid';
import {createAIChatTaskForm} from './taskForms/aiChatTaskForm';
import {createAIVectorStoreTaskForm} from './taskForms/aiVectorStoreTaskForm';
import {createDocumentReaderTaskForm} from './taskForms/documentReaderTaskForm';
import {createJsonConverterTaskForm} from './taskForms/jsonConverterTaskForm';
import {createVectorStoreResourcesForm} from './taskForms/vectorStoreResourcesForm';
import {createEmailTaskForm} from './taskForms/emailTaskForm';
import {createHttpTaskForm} from './taskForms/httpTaskForm';
import {createJQTaskForm} from './taskForms/jqTaskForm';
import {createMapTaskForm} from './taskForms/mapTaskForm';
import {createRetryForm} from './taskForms/retryForm';
import {createRoutingForm} from './taskForms/routingForm';
import {createWaitTaskForm} from './taskForms/waitTaskForm';
import {
    cellSupportsInputParameterControls,
    cellSupportsParallelMetadataRetryControls,
    cellSupportsRetryControls,
    cellSupportsRoutingParameters,
    transitionInputParametersTaskValuePath
} from './taskTabPolicy';

const LOGGER = createLogger("GraphEditorSidebar");

const logUnableToLoadHelpError = error => LOGGER.error("Unable to load help context", error);

const appendJsonExpressionHelpToElement = (id, jsonExpressionHelp) => {
    const element = document.getElementById(id);
    if (!element || element.querySelector('details')) {
        return;
    }

    element.insertAdjacentHTML('beforeend', jsonExpressionHelp);
};

const loadJsonExpressionHelp = (appendHelp, container) => {
    const cleanups = container.inputParametersHelpCleanups ||= new Set();
    import(/* webpackChunkName: "jsonExpressionHelp" */ "./help/jsonExpressionHelp")
        .then(module => {
            if (!container.isConnected || container.inputParametersHelpCleanups !== cleanups) {
                return;
            }
            appendHelp(module.jsonExpressionHelp);
            container.querySelectorAll('details.json-expression-help').forEach(details => {
                if (details.dataset.editorInitialized) {
                    return;
                }
                details.dataset.editorInitialized = 'true';
                cleanups.add(module.initializeJsonExpressionHelp(details));
            });
        })
        .catch(error => logUnableToLoadHelpError(error));
};

const inputParametersJsonAnnotations = (CodeMirror, codeMirror, text = codeMirror.getValue()) => {
    if (isEmpty(text)) {
        return [];
    }

    const getJsonAnnotations = codeMirror.getHelper(CodeMirror.Pos(0, 0), 'lint');
    const annotations = getJsonAnnotations ?
        getJsonAnnotations(text, codeMirror.state?.lint?.linterOptions, codeMirror) :
        [];
    if (!isEmpty(annotations)) {
        return annotations;
    }

    try {
        return isPlainJsonObject(JSON.parse(text)) ?
            [] :
            [{
                from: CodeMirror.Pos(0, 0),
                to: CodeMirror.Pos(0, Math.max(1, String(text).split('\n')[0].length)),
                severity: 'error',
                message: mxResources.get('inputParametersJsonObjectError')
            }];
    } catch (e) {
        return annotations;
    }
};

const inputParametersLintOption = CodeMirror => ({
    getAnnotations: (text, options, codeMirror) =>
        inputParametersJsonAnnotations(CodeMirror, codeMirror, text)
});

const unableToLoadEditor = id => {
    const editorEl = document.getElementById(id);
    if (!editorEl) {
        return;
    }

    const errorMessage = document.createElement('div');
    errorMessage.className = 'ui message error visible';
    errorMessage.textContent = mxResources.get('errorLoadingEditor');
    editorEl.replaceWith(errorMessage);
}

const toPageDropdownResults = pageTitles => pageTitles.map(pageTitle => {
    let icon = 'user';
    if (pageTitle.pageRestrictions === 'VIEW_PUBLIC') {
        icon = 'globe';
    } else if (pageTitle.pageRestrictions === 'PRIVATE') {
        icon = 'lock';
    }
    return {
        value: pageTitle.id,
        name: pageTitle.path,
        icon
    }
});

const prepareHttpTaskErrors = taskErrors => {
    const errors = [];

    appendFieldErrors(errors, taskErrors?.fields?.credentials);
    appendDirectFieldErrors(errors, taskErrors?.fields?.httpRequest);
    appendPrefixedFieldErrors(errors, 'url', taskErrors?.fields?.httpRequest?.url);
    appendPrefixedFieldErrors(errors, 'method', taskErrors?.fields?.httpRequest?.method);
    appendPrefixedFieldErrors(errors, 'headers', taskErrors?.fields?.httpRequest?.headers);
    appendPrefixedFieldErrors(errors, 'body', taskErrors?.fields?.httpRequest?.body);
    appendFieldErrors(errors, taskErrors?.fields?.connectionTimeoutSeconds);
    appendFieldErrors(errors, taskErrors?.fields?.readTimeoutSeconds);

    return errors;
}

const prepareEmailTaskErrors = taskErrors => {
    const errors = [];

    appendDirectFieldErrors(errors, taskErrors?.fields?.email);
    appendPrefixedFieldErrors(errors, 'fromName', taskErrors?.fields?.email?.fromName);
    appendIndexedFieldErrors(errors, 'toAddresses', taskErrors?.fields?.email?.toAddresses);
    appendIndexedFieldErrors(errors, 'ccAddresses', taskErrors?.fields?.email?.ccAddresses);
    appendIndexedFieldErrors(errors, 'bccAddresses', taskErrors?.fields?.email?.bccAddresses);
    appendIndexedFieldErrors(errors, 'replyToAddresses', taskErrors?.fields?.email?.replyToAddresses);
    appendPrefixedFieldErrors(errors, 'subject', taskErrors?.fields?.email?.subject);
    appendPrefixedFieldErrors(errors, 'html', taskErrors?.fields?.email?.html);
    appendPrefixedFieldErrors(errors, 'text', taskErrors?.fields?.email?.text);
    appendIndexedFieldErrors(errors, 'attachments', taskErrors?.fields?.email?.attachments);

    return errors;
}

const collectFieldErrors = fieldError => normalizeFieldErrors(fieldError);

const collectDirectFieldErrors = fieldError => {
    if (!fieldError || typeof fieldError !== 'object' || Array.isArray(fieldError)) {
        return collectFieldErrors(fieldError);
    }

    return collectFieldErrors(fieldError.message);
};

const appendFieldErrors = (errors, fieldError) => {
    errors.push(...collectFieldErrors(fieldError));
};

const appendDirectFieldErrors = (errors, fieldError) => {
    errors.push(...collectDirectFieldErrors(fieldError));
};

const appendPrefixedFieldErrors = (errors, prefix, fieldError) => {
    collectFieldErrors(fieldError).forEach(message => errors.push(`${prefix}: ${message}`));
};

const appendIndexedFieldErrors = (errors, prefix, fieldError) => {
    if (Array.isArray(fieldError)) {
        fieldError.forEach((message, index) => {
            collectFieldErrors(message).forEach(normalizedMessage =>
                errors.push(`${prefix}[${index}]: ${normalizedMessage}`));
        });
        return;
    }

    appendPrefixedFieldErrors(errors, prefix, fieldError);
};

const toCredentialsDropdownResults = credentialsList => ({
    success: true,
    results: credentialsList.map(entry => {
        const oAuth2Status = entry.authorized ?
            mxResources.get('credentialsAuthorizedSuffix') :
            mxResources.get('credentialsNotAuthorizedSuffix');
        const regionStatus = entry.credentials.type === 'AWS' ?
            mxResources.get('credentialsRegionSuffix', [entry.credentials.region]) :
            '';
        const name = `${entry.credentials.name} (${mxResources.get(`credentials${entry.credentials.type}`)}${regionStatus}${entry.credentials.type === 'OAUTH2' ? oAuth2Status : ''})`;
        return {
            value: entry.credentialsId,
            name,
            text: name,
            credentialType: entry.credentials.type,
            authorized: entry.authorized,
            region: entry.credentials.region,
            description: entry.credentials.description,
            descriptionVertical: true
        };
    }),
});

const initializeCredentialsDropdowns = container => {
    container.querySelectorAll('select.ui.dropdown.credentials').forEach(select => {
        if (select.dataset.credentialsDropdownInitialized === 'true') {
            return;
        }

        initializeCredentialsDropdown({
            select, container,
            mxResources, toResults: toCredentialsDropdownResults,
            onChange: value => {
                select.value = value;
                select.dispatchEvent(new Event('change', {bubbles: true}));
            }
        });
    });
};

const dropdownValues = value => {
    if (Array.isArray(value)) {
        return value;
    }

    return (typeof value === 'undefined' || value === null ? '' : String(value))
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
};

const syncSelectDropdownValue = (select, value) => {
    if (!select.multiple) {
        select.value = value;
        return;
    }

    const values = dropdownValues(value);
    values.forEach(item => {
        const optionExists = [...select.querySelectorAll('option')].some(option => option.value === item);
        if (optionExists) {
            return;
        }

        const option = document.createElement('option');
        option.value = item;
        option.textContent = item;
        select.appendChild(option);
    });

    select.querySelectorAll('option').forEach(option => {
        option.selected = values.includes(option.value);
    });
};

const basicDropdownSettings = select => {
    const settings = {duration: 0};

    if (select.id === 'vectorOperation') {
        settings.values = Array.from(select.options).map(option => ({
            value: option.value,
            name: option.textContent,
            text: option.textContent,
            description: option.dataset.description,
            descriptionVertical: true,
            selected: option.selected
        }));
    }

    if (select.classList.contains('addition') || select.classList.contains('additions')) {
        settings.allowAdditions = true;
        settings.hideAdditions = false;
        settings.forceSelection = false;
    }

    return settings;
};

const initializeBasicDropdowns = (container, selector = 'select.ui.dropdown:not(.credentials)') => {
    container.querySelectorAll(selector).forEach(select => {
        if (select.dataset.basicDropdownInitialized === 'true' ||
            select.dataset.credentialsDropdownInitialized === 'true' ||
            select.dataset.chatModelDropdownInitialized === 'true' ||
            select.dataset.embeddingModelProvider !== undefined) {
            return;
        }

        select.dataset.basicDropdownInitialized = 'true';
        const dropdown = $(select).dropdown(basicDropdownSettings(select));
        const selectedOption = select.selectedOptions?.[0];
        if (select.id === 'vectorStoreProvider' && selectedOption?.disabled) {
            dropdown.dropdown('set text', selectedOption.textContent);
        }
    });
};

const initializeAIChatDropdowns = container => {
    initializeCredentialsDropdowns(container);
    initializeAIChatModelDropdowns(container);
    initializeBasicDropdowns(container);
    initializeMCPToolFilterDropdowns(container);
};

const initializeEmailDropdowns = container => {
    initializeAddableMultipleValueDropdowns(
        container,
        '#emailEditor .email-multiple-value-dropdown',
        'emailDropdownInitialized');
};

const initializeAddableMultipleValueDropdowns = (container, selector, initializedDatasetName) => {
    container.querySelectorAll(selector).forEach(dropdown => {
        if (dropdown.dataset[initializedDatasetName] === 'true') {
            return;
        }

        const allowAdditions = dropdown.dataset.allowAdditions !== 'false';
        dropdown.dataset[initializedDatasetName] = 'true';
        $(dropdown).dropdown({
            allowAdditions,
            duration: 0,
            forceSelection: !allowAdditions,
            fullTextSearch: true,
            hideAdditions: !allowAdditions,
            onChange: value => {
                const input = dropdown.querySelector('input');
                if (!input) {
                    return;
                }

                input.value = Array.isArray(value) ? value.join(',') : (value || '');
                setTimeout(() => {
                    if (input.isConnected === false) {
                        return;
                    }
                    input.dispatchEvent(new Event('change', {bubbles: true}));
                });
            }
        });
    });
};

const hasMCPToolDiscoveryFields = dropdown => [
    dropdown.dataset.mcpServerTransportType,
    dropdown.dataset.mcpServerUrl
].every(value => !isEmpty(value));

const mcpToolDiscoveryPayload = dropdown => ({
    credentials: dropdown.dataset.mcpServerCredentials || '',
    transportType: dropdown.dataset.mcpServerTransportType || '',
    serverUrl: dropdown.dataset.mcpServerUrl || '',
    requestTimeoutSeconds: isEmpty(dropdown.dataset.mcpServerRequestTimeoutSeconds)
        ? null
        : Number.parseInt(dropdown.dataset.mcpServerRequestTimeoutSeconds, 10)
});

const isAzureOpenAIProvider = provider => provider === 'AzureOpenAI';

const hasChatModelListingFields = dropdown => {
    if (isEmpty(dropdown.dataset.chatModelCredentials) || isEmpty(dropdown.dataset.chatModelProvider)) {
        return false;
    }

    return !isAzureOpenAIProvider(dropdown.dataset.chatModelProvider) ||
        !isEmpty(dropdown.dataset.chatModelEndpoint);
};

const chatModelListingPayload = dropdown => ({
    credentials: dropdown.dataset.chatModelCredentials || '',
    provider: dropdown.dataset.chatModelProvider || '',
    endpoint: dropdown.dataset.chatModelEndpoint || null
});

const toChatModelDropdownResults = (dropdown, response) => {
    const models = Array.isArray(response) ? response : [];
    dropdown.dataset.chatModelSuggestionsLoaded = 'true';
    dispatchChatModelListingEvent(dropdown, 'chat-models-listed', {
        models,
        render: false
    });
    return {
        success: true,
        results: models
            .map(model => {
                const value = String(model?.id || '').trim();
                const name = String(model?.label || model?.name || value).trim();
                if (value.length === 0) {
                    return null;
                }
                return {
                    value,
                    name,
                    text: name || value
                };
            })
            .filter(Boolean)
    };
};

const dispatchChatModelListingEvent = (dropdown, type, detail) => {
    const event = typeof CustomEvent === 'function'
        ? new CustomEvent(type, {bubbles: true, detail})
        : {type, bubbles: true, detail};
    dropdown.dispatchEvent(event);
};

const discoveryFailureMessage = (response, fallback) => {
    const body = response?.responseJSON || response;
    const status = Number.isInteger(body?.status) ? body.status : null;
    const error = typeof body?.error === 'string' ? body.error : '';
    const message = typeof body?.message === 'string' ? body.message : fallback;
    const label = [status === null ? '' : `HTTP ${status}`, error].filter(Boolean).join(' ');
    return label ? `${label}: ${message}` : message;
};

const dispatchChatModelListingError = (dropdown, response) => {
    dispatchChatModelListingEvent(dropdown, 'chat-models-listing-error', {
        message: discoveryFailureMessage(response, mxResources.get('chatModelListingFailed')),
        render: false
    });
};

const setModelListingRequestHeaders = xhr => {
    xhr.setRequestHeader('Content-Type', 'application/json');
    const csrfHeaderName = document.querySelector('meta[name="swf-csrf-header-name"]')?.getAttribute('content');
    const csrfToken = document.querySelector('meta[name="swf-csrf-token"]')?.getAttribute('content');
    if (csrfHeaderName && csrfToken) {
        xhr.setRequestHeader(csrfHeaderName, csrfToken);
    }
    return xhr;
};

const initializeAIChatModelDropdowns = container => {
    container.querySelectorAll('#aiChatModelEditor .chat-model-dropdown').forEach(dropdown => {
        if (dropdown.dataset.chatModelDropdownInitialized === 'true') {
            return;
        }

        const settings = {
            allowAdditions: true,
            duration: 0,
            forceSelection: false,
            fullTextSearch: true,
            hideAdditions: false,
            minCharacters: 0,
            onChange: value => {
                syncSelectDropdownValue(dropdown, value || '');
                dropdown.dispatchEvent(new Event('change', {bubbles: true}));
            }
        };

        const disabled = dropdown.className?.split(/\s+/).includes('disabled');
        if (!disabled && hasChatModelListingFields(dropdown) && dropdown.dataset.chatModelSuggestionsLoaded !== 'true') {
            settings.filterRemoteData = true;
            settings.saveRemoteData = true;
            settings.apiSettings = {
                url: '/api/ai-chat/models',
                method: 'POST',
                cache: false,
                rawResponse: true,
                beforeSend: settings => {
                    if (!hasChatModelListingFields(dropdown) || dropdown.dataset.chatModelSuggestionsLoaded === 'true') {
                        return false;
                    }
                    settings.data = stringifyJSON(chatModelListingPayload(dropdown));
                    return settings;
                },
                beforeXHR: setModelListingRequestHeaders,
                onResponse: response => toChatModelDropdownResults(dropdown, response),
                onFailure: response => dispatchChatModelListingError(dropdown, response),
                onError: response => dispatchChatModelListingError(dropdown, response)
            };
        }

        dropdown.dataset.chatModelDropdownInitialized = 'true';
        const chatModelDropdown = $(dropdown).dropdown(settings);
        if (settings.apiSettings) {
            chatModelDropdown.dropdown('queryRemote', '', function () {
                chatModelDropdown.dropdown('set selected', dropdown.value);
            });
        }
    });
};

const initializeAIEmbeddingModelDropdowns = container => {
    container.querySelectorAll('#workflowVectorStoresEditor select[data-embedding-model-provider]').forEach(dropdown => {
        if (dropdown.dataset.embeddingModelDropdownInitialized === 'true') {
            return;
        }
        const settings = {
            allowAdditions: true,
            duration: 0,
            forceSelection: false,
            fullTextSearch: true,
            hideAdditions: false,
            minCharacters: 0,
            onChange: value => {
                syncSelectDropdownValue(dropdown, value || '');
                dropdown.dispatchEvent(new Event('change', {bubbles: true}));
            }
        };
        if (!dropdown.disabled && dropdown.dataset.embeddingModelCredentials &&
            dropdown.dataset.embeddingModelProvider &&
            dropdown.dataset.embeddingModelSuggestionsLoaded !== 'true') {
            settings.filterRemoteData = true;
            settings.saveRemoteData = true;
            settings.apiSettings = {
                url: '/api/ai-embedding/models', method: 'POST', cache: false, rawResponse: true,
                beforeSend: requestSettings => {
                    if (!dropdown.dataset.embeddingModelCredentials || !dropdown.dataset.embeddingModelProvider ||
                        dropdown.dataset.embeddingModelSuggestionsLoaded === 'true') {
                        return false;
                    }
                    requestSettings.data = stringifyJSON({
                        credentials: dropdown.dataset.embeddingModelCredentials,
                        provider: dropdown.dataset.embeddingModelProvider
                    });
                    return requestSettings;
                },
                beforeXHR: setModelListingRequestHeaders,
                onResponse: response => {
                    const models = Array.isArray(response) ? response : [];
                    dropdown.dataset.embeddingModelSuggestionsLoaded = 'true';
                    dispatchChatModelListingEvent(dropdown, 'embedding-models-listed', {models, render: false});
                    disableMCPToolRemoteDiscovery(dropdown.parentElement);
                    return {success: true, results: models.map(model => ({
                        value: model.id, name: model.label || model.id, text: model.label || model.id
                    }))};
                },
                onFailure: () => dispatchChatModelListingEvent(dropdown, 'embedding-models-listing-error', {
                    message: mxResources.get('embeddingModelListingFailed')
                }),
                onError: () => dispatchChatModelListingEvent(dropdown, 'embedding-models-listing-error', {
                    message: mxResources.get('embeddingModelListingFailed')
                })
            };
        }
        dropdown.dataset.embeddingModelDropdownInitialized = 'true';
        const modelDropdown = $(dropdown).dropdown(settings);
        if (settings.apiSettings) {
            modelDropdown.dropdown('queryRemote', '', function () {
                modelDropdown.dropdown('set selected', dropdown.value);
            });
        }
    });
};

const toMCPToolDropdownResults = (dropdown, response) => {
    const tools = Array.isArray(response) ? response : [];
    dropdown.dataset.mcpToolsDiscovered = 'true';
    dispatchMCPToolDiscoveryEvent(dropdown, 'mcp-tools-discovered', {
        tools,
        render: false
    });
    disableMCPToolRemoteDiscovery(dropdown);
    return {
        success: true,
        results: tools.map(tool => ({
            value: tool.name,
            name: tool.name,
            text: tool.name,
            description: tool.description,
            descriptionVertical: true
        }))
    };
};

const disableMCPToolRemoteDiscovery = dropdown => {
    if (typeof $ !== 'function' || !$.fn?.dropdown) {
        return;
    }

    const dropdownModule = $(dropdown);
    dropdownModule.dropdown('setting', {
        apiSettings: false,
        filterRemoteData: false,
        saveRemoteData: false
    });
};

const dispatchMCPToolDiscoveryEvent = (dropdown, type, detail) => {
    const event = typeof CustomEvent === 'function'
        ? new CustomEvent(type, {bubbles: true, detail})
        : {type, bubbles: true, detail};
    dropdown.dispatchEvent(event);
};

const dispatchMCPToolDiscoveryError = (dropdown, response) => {
    dispatchMCPToolDiscoveryEvent(dropdown, 'mcp-tools-discovery-error', {
        message: discoveryFailureMessage(response, mxResources.get('mcpServerToolsDiscoveryFailed'))
    });
};

const initializeMCPToolFilterDropdowns = container => {
    container.querySelectorAll('#aiChatMCPServersEditor .mcp-tool-filter-dropdown').forEach(dropdown => {
        if (dropdown.dataset.mcpToolFilterDropdownInitialized === 'true') {
            return;
        }

        const settings = {
            preserveHTML: false,
            allowAdditions: true,
            duration: 0,
            transition: {showMethod: 'none', hideMethod: 'none'},
            forceSelection: false,
            fullTextSearch: true,
            hideAdditions: false,
            showNoResults: false,
            onChange: value => {
                const input = dropdown.querySelector('input');
                if (!input) {
                    return;
                }

                input.value = Array.isArray(value) ? value.join(',') : (value || '');
                setTimeout(() => {
                    if (input.isConnected === false) {
                        return;
                    }
                    input.dispatchEvent(new Event('change', {bubbles: true}));
                });
            }
        };

        if (hasMCPToolDiscoveryFields(dropdown) && dropdown.dataset.mcpToolsDiscovered !== 'true') {
            settings.filterRemoteData = true;
            settings.saveRemoteData = true;
            settings.apiSettings = {
                url: '/api/ai-chat-test/mcp-server/tools',
                method: 'POST',
                contentType: 'application/json',
                processData: false,
                cache: false,
                rawResponse: true,
                beforeSend: settings => {
                    if (!hasMCPToolDiscoveryFields(dropdown) || dropdown.dataset.mcpToolsDiscovered === 'true') {
                        return false;
                    }
                    settings.data = stringifyJSON(mcpToolDiscoveryPayload(dropdown));
                    return settings;
                },
                beforeXHR: function (xhr) {
                    xhr.setRequestHeader('Content-Type', 'application/json');
                    return xhr;
                },
                onResponse: response => toMCPToolDropdownResults(dropdown, response),
                onFailure: response => dispatchMCPToolDiscoveryError(dropdown, response),
                onError: response => dispatchMCPToolDiscoveryError(dropdown, response)
            };
        }

        dropdown.dataset.mcpToolFilterDropdownInitialized = 'true';
        $(dropdown).dropdown(settings);
    });
};

const aiChatTabPaths = [
    'taskTabAIChatMessages',
    'taskTabAIChatModel',
    'taskTabAIChatMCPServers'
];

const isAIChatTabPath = tabPath => aiChatTabPaths.includes(tabPath);

const refreshMountedCodeEditor = editorId => {
    document.querySelector(`#${editorId} .CodeMirror`)?.CodeMirror?.refresh?.();
};

const tabEditorRefreshers = {
    workflowErrorHandling: () => refreshMountedCodeEditor('failureWorkflowInputExample'),
    taskTabAIChatMessages: container => container.aiChatTaskForm?.refreshActiveMessageEditor?.(),
    taskTabAIVectorStore: container => container.aiVectorStoreTaskForm?.refresh?.(),
    taskTabEmail: container => container.emailTaskForm?.refreshActiveBodyEditor?.(),
    taskTabWorkflowOutput: () => refreshMountedCodeEditor('workflowOutputEditor'),
    taskTabInputParameters: () => refreshMountedCodeEditor('inputParametersEditor'),
    taskRouting: () => refreshMountedCodeEditor('outgoingRoutingInputParametersEditor'),
    routingProperties: () => refreshMountedCodeEditor('routingConditionEditor')
};

const refreshActiveTabEditor = (container, tabPath, isCurrentTab = () => true) => {
    const refreshEditor = tabEditorRefreshers[tabPath];
    if (!refreshEditor) {
        return;
    }

    setTimeout(() => {
        if (isCurrentTab(tabPath)) {
            refreshEditor(container);
        }
    });
};

const getLazyEditorMountElement = id => {
    const editorElement = document.getElementById(id);
    if (!editorElement ||
        editorElement.dataset.editorLoading === 'true' ||
        editorElement.dataset.editorMounted === 'true') {
        return null;
    }

    editorElement.dataset.editorLoading = 'true';
    return editorElement;
};

const isCurrentLazyEditorMount = (id, editorElement) => document.getElementById(id) === editorElement;

const mountedEditorField = field => {
    field.dataset.editorMounted = 'true';
    return field;
};

const unableToLoadCurrentEditor = (id, editorElement) => {
    if (isCurrentLazyEditorMount(id, editorElement)) {
        unableToLoadEditor(id);
    }
};

const aiChatFields = ['userMessages', 'systemMessages', 'chatModel', 'mcpServers'];

const applyAIChatToTask = (task, aiChat) => {
    aiChatFields.forEach(fieldName => delete task[fieldName]);
    Object.assign(task, aiChat);
    return task;
};

const initializeWorkflowDefinitionVersionDropdown = ({element, workflowDefinitionId, value, onChange}) => {
    const dropdown = $(element);
    const selectElement = element.matches('select') ? element : element.querySelector('select');
    if (dropdown.data('module-dropdown')) {
        dropdown.dropdown('destroy');
    }

    if (isEmpty(workflowDefinitionId)) {
        if (selectElement) {
            selectElement.value = '';
            selectElement.disabled = true;
            selectElement.setAttribute('disabled', '');
        }
        dropdown.dropdown({
            duration: 0,
            clearable: true
        });
        return;
    }

    if (selectElement) {
        selectElement.disabled = false;
        selectElement.removeAttribute('disabled');
    }
    workflowDefinitionVersionsSelectDropdown({
        element,
        workflowDefinitionId,
        value,
        onChange
    });
};

const handleWorkflowErrorHandlingTab = (container, model, workflow, currentWorkflowDefinition) => {
    const exampleElement = getLazyEditorMountElement('failureWorkflowInputExample');
    if (exampleElement) {
        const example = exampleElement.querySelector('code').textContent;
        const retainFallback = () => {
            if (isCurrentLazyEditorMount('failureWorkflowInputExample', exampleElement)) {
                delete exampleElement.dataset.editorLoading;
            }
        };
        import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor')
            .then(module => module.codeEditor({
                callbackFn: editor => {
                    if (!isCurrentLazyEditorMount('failureWorkflowInputExample', exampleElement)) {
                        editor.getWrapperElement()?.remove?.();
                        return;
                    }
                    editor.setValue(example);
                    exampleElement.replaceChildren(editor.getWrapperElement());
                    editor.setSize(null, 'auto');
                    mountedEditorField(exampleElement);
                    editor.refresh();
                },
                errorFn: retainFallback,
                options: {
                    mode: 'application/json',
                    readOnly: true,
                    lineWrapping: true,
                    viewportMargin: Infinity,
                    foldGutter: true,
                    gutters: ['CodeMirror-lint-markers', 'CodeMirror-foldgutter']
                }
            }))
            .catch(retainFallback);
    }
    const failureWorkflow = workflow.failureWorkflow || '';
    const failureWorkflowVersion = Number(workflow.failureWorkflowVersion) || 0;
    const failureWorkflowVersionElement = container.querySelector('.ui.dropdown.failureWorkflowVersion');

    const failureWorkflowDropdown = $(container.querySelector('.ui.dropdown.failureWorkflow')).dropdown({
        duration: 0,
        filterRemoteData: true,
        saveRemoteData: false,
        fullTextSearch: true,
        clearable: true,
        apiSettings: {
            url: '/api/workflow-definitions?size=1000',
            cache: false,
            rawResponse: true,
            onResponse: ({content: workflowDefinitions}) => ({
                success: true,
                results: workflowDefinitions.filter(item => !item.disabled && item.id !== currentWorkflowDefinition.id).map(entry => {
                    return {
                        value: entry.id,
                        name: entry.name,
                    };
                }),
            }),
        },
        onChange: value => {
            if (value !== (workflow.failureWorkflow || '')) {
                workflow.failureWorkflow = value;
                workflow.failureWorkflowVersion = null;
                updateRootValue(model, {
                    failureWorkflow: workflow.failureWorkflow,
                    failureWorkflowVersion: workflow.failureWorkflowVersion
                });
                initializeWorkflowDefinitionVersionDropdown({
                    element: failureWorkflowVersionElement,
                    workflowDefinitionId: value,
                    value: '',
                    onChange: version => {
                        if (Number(version) === (Number(workflow.failureWorkflowVersion) || 0)) {
                            return;
                        }
                        workflow.failureWorkflowVersion = isEmpty(version) ? null : version;
                        updateRootValue(model, {failureWorkflowVersion: workflow.failureWorkflowVersion});
                    }
                });
            }
        }
    });

    initializeWorkflowDefinitionVersionDropdown({
        element: failureWorkflowVersionElement,
        workflowDefinitionId: failureWorkflow,
        value: failureWorkflowVersion > 0 ? String(failureWorkflowVersion) : '',
        onChange: value => {
            if (Number(value) === (Number(workflow.failureWorkflowVersion) || 0)) {
                return;
            }
            workflow.failureWorkflowVersion = isEmpty(value) ? null : value;
            updateRootValue(model, {failureWorkflowVersion: workflow.failureWorkflowVersion});
        }
    });

    if (!isEmpty(failureWorkflow)) {
        failureWorkflowDropdown.dropdown('queryRemote', '', function () {
            failureWorkflowDropdown.dropdown('set selected', failureWorkflow);
        });

        api(`/api/workflow-definition-geometry/${failureWorkflow}${failureWorkflowVersion > 0 ? `?version=${failureWorkflowVersion}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            },
        }).then(workflowDefinitionWithGeometry => {
            const graphContent = document.getElementById('failureWorkflowDiagram');
            const editorUi = new EditorUi(new Editor(false), graphContent,
                {
                    id: null,
                    version: 0,
                    currentVersion: 0,
                }, false);
            editorUi.editor.initializeWorkflow(fromWorkflowDefinition(workflowDefinitionWithGeometry));
            editorUi.actions.get('save').setEnabled(false);
            graphContent.style.height = '400px';
            graphContent.style.display = '';
            editorUi.actions.get('resetView').funct();
        }).catch(error => LOGGER.error('Unable to fetch failure workflow definition', error));
    }
}

const hasPageVersion = pageVersion => pageVersion !== undefined && pageVersion !== null && pageVersion !== '';
const pageReferencePageId = reference => normalizePageReference(reference)?.pageId || '';
const pageReferenceVersion = reference => {
    const pageVersion = normalizePageReference(reference)?.pageVersion;
    return hasPageVersion(pageVersion) ? String(pageVersion) : '';
};
const createPageReference = (pageId, pageVersion = null) => isEmpty(pageId)
    ? null
    : {
        pageId,
        pageVersion: hasPageVersion(pageVersion) ? pageVersion : null
    };

const previewPageUrl = reference => {
    const normalized = normalizePageReference(reference);
    if (normalized === null) {
        return null;
    }

    const query = new URLSearchParams({embed: 'true'});
    if (hasPageVersion(normalized.pageVersion)) {
        query.set('version', normalized.pageVersion);
    }
    return `/pages/${normalized.pageId}?${query.toString()}`;
};

const removePagePreview = container => {
    container.querySelector('.workflow-page-preview')?.remove();
};

const removeInactivePagePreviews = (container, activeTabPath) => {
    container.querySelectorAll('.workflow-page-preview').forEach(preview => {
        if (preview.closest(`.tab[data-tab="${activeTabPath}"]`) === null) {
            preview.remove();
        }
    });
};

const setupPagePreviewComponents = previewContainer => {
    setupDropdowns(previewContainer);
    $(previewContainer.querySelectorAll('.ui.calendar')).calendar({
        today: true
    });
    $(previewContainer.querySelectorAll('.ui.checkbox')).checkbox();
    $(previewContainer.querySelectorAll('.ui.menu .item[data-tab]')).tab({
        duration: 0
    });
    setupCloseableMessages(previewContainer);
    previewContainer.querySelectorAll('.markdown-body .ui.accordion').forEach(element => {
        const title = element.querySelector('.title');
        if (title && !title.querySelector('.expand')) {
            title.insertAdjacentHTML('afterbegin', '<i class="dropdown icon expand"></i>');
        }
        $(element).accordion({
            selector: {
                trigger: '.title .expand'
            },
            duration: 0,
            animateChildren: false,
        });
    });
};

const pageVersionDropdownElement = element => element.matches('.ui.dropdown')
    ? element
    : element.closest('.ui.dropdown') || element;

const initializePageVersionDropdown = ({element, pageId, pageVersion, onChange}) => {
    const dropdownElement = pageVersionDropdownElement(element);
    const selectElement = dropdownElement.matches('select') ? dropdownElement : dropdownElement.querySelector('select');
    const dropdown = $(dropdownElement);
    if (dropdown.data('module-dropdown')) {
        dropdown.dropdown('destroy');
    }

    if (isEmpty(pageId)) {
        if (selectElement) {
            selectElement.value = '';
            selectElement.disabled = true;
            selectElement.setAttribute('disabled', '');
        }
        dropdown.dropdown({
            duration: 0,
            clearable: true,
            searchable: true
        });
        return;
    }

    if (selectElement) {
        selectElement.disabled = false;
        selectElement.removeAttribute('disabled');
    }
    pageVersionsSelectDropdown({
        element: dropdownElement,
        pageId,
        value: pageVersion,
        onChange
    });
};

const handleWorkflowStartFormTab = (container, model, currentWorkflowDefinition) => {
    const tabContainer = container.querySelector('#workflowStartFormContainer') || container;
    if (!isEmpty(currentWorkflowDefinition.templateLaunchPage)) {
        $(tabContainer.querySelector('.ui.dropdown.workflowStartPage')).dropdown({duration: 0});
        $(tabContainer.querySelector('.ui.dropdown.workflowStartPageVersion')).dropdown({duration: 0});
        return;
    }
    const workflowStartPage = getWorkflowStartPage(model);
    const workflowStartPageVersionElement = tabContainer.querySelector('.ui.dropdown.workflowStartPageVersion');
    const workflowStartPageDropdown = $(tabContainer.querySelector('.ui.dropdown.workflowStartPage')).dropdown({
        duration: 0,
        filterRemoteData: true,
        saveRemoteData: false,
        fullTextSearch: true,
        clearable: true,
        apiSettings: {
            url: '/api/pages',
            cache: false,
            rawResponse: true,
            onResponse: pageTitles => ({
                success: true,
                results: toPageDropdownResults(pageTitles),
            }),
        },
        onChange: value => {
            if (value !== pageReferencePageId(getWorkflowStartPage(model))) {
                setWorkflowStartPage(model, createPageReference(value));
                appendPagePreview(tabContainer, getWorkflowStartPage(model));
                initializePageVersionDropdown({
                    element: workflowStartPageVersionElement,
                    pageId: value,
                    pageVersion: '',
                    onChange: pageVersion => {
                        const pageId = pageReferencePageId(getWorkflowStartPage(model));
                        if (String(pageVersion || '') === pageReferenceVersion(getWorkflowStartPage(model))) {
                            return;
                        }
                        setWorkflowStartPage(model, createPageReference(pageId, pageVersion));
                        appendPagePreview(tabContainer, getWorkflowStartPage(model));
                    }
                });
            }
        }
    });

    if (!isEmpty(pageReferencePageId(workflowStartPage))) {
        workflowStartPageDropdown.dropdown('queryRemote', '', function () {
            workflowStartPageDropdown.dropdown('set selected', pageReferencePageId(workflowStartPage));
        });
    }

    initializePageVersionDropdown({
        element: workflowStartPageVersionElement,
        pageId: pageReferencePageId(workflowStartPage),
        pageVersion: pageReferenceVersion(workflowStartPage),
        onChange: pageVersion => {
            const pageId = pageReferencePageId(getWorkflowStartPage(model));
            if (String(pageVersion || '') === pageReferenceVersion(getWorkflowStartPage(model))) {
                return;
            }
            setWorkflowStartPage(model, createPageReference(pageId, pageVersion));
            appendPagePreview(tabContainer, getWorkflowStartPage(model));
        }
    });
    appendPagePreview(tabContainer, getWorkflowStartPage(model));
}

const appendPagePreview = (container, processor) => {
    const url = previewPageUrl(processor);
    removePagePreview(container);
    if (isEmpty(url)) {
        return;
    }

    const previewContainer = document.createElement('div');
    previewContainer.className = 'workflow-page-preview';
    container.appendChild(previewContainer);

    api(url, {
        method: 'GET',
        headers: {
            'Accept': 'text/html'
        }
    })
        .then(pageContent => {
            if (previewContainer.parentNode === null) {
                return;
            }
            previewContainer.insertAdjacentHTML('beforeend', pageContent);
            setupPagePreviewComponents(previewContainer);
        })
        .catch(error => LOGGER.error("Unable to load task processor page", error));
}

const hasTemplateTaskProcessorPage = (currentWorkflowDefinition, task) =>
    typeof task.templateSourceTaskName === 'string' &&
    Object.hasOwn(currentWorkflowDefinition.templateProcessorPages || {}, task.templateSourceTaskName);

const handleTaskProcessorTab = (container, model, cell, task, currentWorkflowDefinition) => {
    const tabContainer = container.querySelector('#taskProcessorContainer') || container;
    if (hasTemplateTaskProcessorPage(currentWorkflowDefinition, task)) {
        $(tabContainer.querySelector('.ui.dropdown.processorPage')).dropdown({duration: 0});
        $(tabContainer.querySelector('.ui.dropdown.processorPageVersion')).dropdown({duration: 0});
        return;
    }
    const processorVersionElement = tabContainer.querySelector('.ui.dropdown.processorPageVersion');
    const processorDropdown = $(tabContainer.querySelector('.ui.dropdown.processorPage')).dropdown({
        duration: 0,
        filterRemoteData: true,
        saveRemoteData: false,
        fullTextSearch: true,
        clearable: true,
        apiSettings: {
            url: '/api/pages',
            cache: false,
            rawResponse: true,
            onResponse: pageTitles => ({
                success: true,
                results: toPageDropdownResults(pageTitles),
            }),
        },
        onChange: value => {
            if (value !== pageReferencePageId(task.processor)) {
                task.processor = createPageReference(value);
                updateCellValue(model, cell, {processor: task.processor});
                appendPagePreview(tabContainer, task.processor);
                initializePageVersionDropdown({
                    element: processorVersionElement,
                    pageId: value,
                    pageVersion: '',
                    onChange: pageVersion => {
                        if (String(pageVersion || '') === pageReferenceVersion(task.processor)) {
                            return;
                        }
                        task.processor = createPageReference(pageReferencePageId(task.processor), pageVersion);
                        updateCellValue(model, cell, {processor: task.processor});
                        appendPagePreview(tabContainer, task.processor);
                    }
                });
            }
        }
    });

    const processor = normalizePageReference(task.processor);
    if (!isEmpty(pageReferencePageId(processor))) {
        processorDropdown.dropdown('queryRemote', '', function () {
            processorDropdown.dropdown('set selected', pageReferencePageId(processor));
        });
    }

    initializePageVersionDropdown({
        element: processorVersionElement,
        pageId: pageReferencePageId(processor),
        pageVersion: pageReferenceVersion(processor),
        onChange: pageVersion => {
            if (String(pageVersion || '') === pageReferenceVersion(task.processor)) {
                return;
            }
            task.processor = createPageReference(pageReferencePageId(task.processor), pageVersion);
            updateCellValue(model, cell, {processor: task.processor});
            appendPagePreview(tabContainer, task.processor);
        }
    });
    appendPagePreview(tabContainer, task.processor);
}

const handleWorkflowOutputTab = (container, editable, sidebar, model, cell, task, taskErrors) => {
    const editorElement = getLazyEditorMountElement('workflowOutputEditor');
    if (!editorElement) {
        return;
    }

    import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor')
        .then(module => module.codeEditor({
            callbackFn: editor => {
                if (!isCurrentLazyEditorMount('workflowOutputEditor', editorElement)) {
                    editor.getWrapperElement()?.remove?.();
                    return;
                }

                editor.on('blur', codeMirror => {
                    codeMirror.setOption('lint', !isEmpty(codeMirror.getValue()));

                    const getAnnotations = codeMirror.getHelper(module.CodeMirror.Pos(0, 0), 'lint');
                    const annotations = getAnnotations(codeMirror.getValue(), codeMirror.state?.lint?.linterOptions, codeMirror);

                    syncJsonFallbackValue({
                        text: codeMirror.getValue(),
                        currentValue: task.workflowOutput,
                        annotations,
                        onValidJson: workflowOutput => {
                            task.workflowOutput = workflowOutput;
                            updateCellValue(model, cell, {workflowOutput: task.workflowOutput});
                        }
                    });
                });

                const fieldElement = editor.getWrapperElement();
                fieldElement.classList.add('ui', 'segment');

                editorElement.replaceWith(mountedEditorField(sidebar.createField({
                    id: 'workflowOutputEditor',
                    label: mxResources.get('workflowOutput'),
                    error: taskErrors?.fields?.workflowOutput,
                    fieldElement
                })));

                loadJsonExpressionHelp(jsonExpressionHelp =>
                    appendJsonExpressionHelpToElement('workflowOutputEditor', jsonExpressionHelp), sidebar.container);

                editor.refresh();
            },
            errorFn: () => unableToLoadCurrentEditor('workflowOutputEditor', editorElement),
            options: {
                readOnly: !editable ? 'nocursor' : false,
                mode: 'application/json',
                lint: !isEmpty(task.workflowOutput),
                foldGutter: true,
                gutters: ['CodeMirror-lint-markers', 'CodeMirror-foldgutter'],
                value: stringifyJsonFallbackValue(task.workflowOutput)
            }
        })).catch(() => unableToLoadCurrentEditor('workflowOutputEditor', editorElement));
}

function handleInputParametersTab(container, editable, sidebar, model, cell, task, taskErrors, taskValuePath, editorId, label = mxResources.get('inputParameters'), helpLabel = 'Input parameters') {
    const editorElement = getLazyEditorMountElement(editorId);
    if (!editorElement) {
        return;
    }

    const inputParametersTask = taskValuePath ? task[taskValuePath] || {} : task;
    const inputParametersErrors = taskValuePath ? taskErrors?.fields?.[taskValuePath] : taskErrors?.fields;

    import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor').then(module => module.codeEditor({
        callbackFn: editor => {
            if (!isCurrentLazyEditorMount(editorId, editorElement)) {
                editor.getWrapperElement()?.remove?.();
                return;
            }

            editor.on('blur', codeMirror => {
                codeMirror.setOption('lint', inputParametersLintOption(module.CodeMirror));
                const annotations = inputParametersJsonAnnotations(module.CodeMirror, codeMirror);

                syncJsonFallbackValue({
                    text: codeMirror.getValue(),
                    currentValue: inputParametersTask.inputParameters,
                    annotations,
                    isValidValue: isBlankOrJsonObject,
                    onValidJson: inputParameters => {
                        inputParametersTask.inputParameters = inputParameters;
                        updateCellValue(
                            model,
                            cell,
                            taskValuePath
                                ? {[taskValuePath]: inputParametersTask}
                                : {inputParameters: inputParametersTask.inputParameters});
                    }
                });
            });

            const fieldElement = editor.getWrapperElement();
            fieldElement.classList.add('ui', 'segment');

            editorElement.replaceWith(mountedEditorField(sidebar.createField({
                id: editorId,
                label,
                error: inputParametersErrors?.inputParameters,
                fieldElement
            })));

            import(/* webpackChunkName: "inputParametersHelp" */ "./help/inputParametersHelp")
                .then(module => {
                    const currentField = document.getElementById(editorId);
                    if (currentField?.contains(fieldElement) && !currentField.querySelector(':scope > details')) {
                        currentField.insertAdjacentHTML('beforeend', module.inputParametersHelp(helpLabel));
                        sidebar.container.inputParametersHelpCleanups ||= new Set();
                        sidebar.container.inputParametersHelpCleanups.add(module.initializeInputParametersHelp(currentField.querySelector(':scope > details')));
                    }
                })
                .catch(error => logUnableToLoadHelpError(error));

            editor.refresh();
        },
        errorFn: () => unableToLoadCurrentEditor(editorId, editorElement),
        options: {
            readOnly: !editable ? 'nocursor' : false,
            mode: 'application/json',
            lint: inputParametersLintOption(module.CodeMirror),
            foldGutter: true,
            gutters: ['CodeMirror-lint-markers', 'CodeMirror-foldgutter'],
            value: stringifyJsonFallbackValue(inputParametersTask.inputParameters)
        }
    })).catch(() => unableToLoadCurrentEditor(editorId, editorElement));
}

const createButtonField = ({id, buttonId, label, className = 'ui mini compact button'}) => {
    const button = document.createElement('button');
    button.id = buttonId;
    button.type = 'button';
    button.className = className;
    button.textContent = label;

    const field = document.createElement('div');
    field.id = id;
    field.className = 'field';
    field.appendChild(button);
    return field;
};

const testAIChatPlacementIds = {
    messages: 'Messages',
    model: 'Model',
    mcpServers: 'MCPServers'
};

const setTestButtonLoading = (button, loading) => {
    button.classList.toggle('loading', loading);
    button.classList.toggle('disabled', loading);
    button.disabled = loading;
    if (loading) {
        button.setAttribute('disabled', '');
    } else {
        button.removeAttribute('disabled');
    }
};

const renderTestResponseField = ({id, label, error = false, response, afterElement}) => {
    document.getElementById(id)?.remove();

    import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor')
        .then(module => createTestResponseField({
            id,
            label,
            error,
            response,
            codeEditorModule: module,
            afterElement
        }))
        .catch(() => createTestResponseField({
            id,
            label,
            error,
            response,
            afterElement
        }));
};

const handleHttpTab = (container, editable, sidebar, model, cell, task, taskErrors) => {
    document.getElementById('testHttpRequestField')?.remove();
    document.getElementById('testHttpResponse')?.remove();

    initializeCredentialsDropdown({
        select: container.querySelector('#credentials'), container,
        mxResources, toResults: toCredentialsDropdownResults,
        onChange: value => {
            const credentials = task.credentials;
            if (value !== credentials) {
                task.credentials = value;
                updateCellValue(model, cell, {credentials: task.credentials});
            }
        }
    });

    const httpTaskForm = createHttpTaskForm({
        task,
        taskErrors,
        editable,
        mxResources,
        onHttpRequestChange: httpRequest => {
            task.httpRequest = httpRequest;
            updateCellValue(model, cell, {httpRequest: task.httpRequest});
        }
    });

    document.getElementById('httpRequestEditor').replaceWith(httpTaskForm.element);
    loadJsonExpressionHelp(jsonExpressionHelp =>
        httpTaskForm.appendJsonExpressionHelp?.(jsonExpressionHelp), container);
    if (editable) {
        const httpRequestDropdowns = [...document.querySelectorAll('#httpRequestEditor .ui.dropdown')];
        const headerNameDropdowns = httpRequestDropdowns.filter(dropdown => dropdown.classList.contains('http-request-header-name'));
        const standardDropdowns = httpRequestDropdowns.filter(dropdown => !dropdown.classList.contains('http-request-header-name'));

        $(standardDropdowns).dropdown({
            duration: 0
        });
        $(headerNameDropdowns).dropdown({
            allowAdditions: true,
            duration: 0,
            forceSelection: false,
            fullTextSearch: true,
            hideAdditions: false
        });
    }

    import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor')
        .then(module => httpTaskForm.mountBodyJsonEditor(module))
        .catch(() => unableToLoadEditor('httpRequestBodyJsonEditor'));

    $(document.querySelector('#httpRequestEditor #openApisSearch'))?.search({
        type: 'category',
        minCharacters: 1,
        duration: 0,
        transition: 'scale',
        onSelect: result => {
            const {servers, slug, apiPath} = result;
            const credentialsSelect = container.querySelector('#credentials');
            const credentialsResponse = $(credentialsSelect.closest('.ui.dropdown') || credentialsSelect)
                .api('read cached response', '/api/credentials');
            const credentialsItem = credentialsResponse?.results?.find(item => item.value === credentialsSelect.value);
            const credentialsRegion = credentialsItem?.credentialType === 'AWS' ? credentialsItem.region : undefined;

            const server = servers[0];
            let base = server.url;
            server.variables?.forEach(variable => {
                if (variable.name === 'region' && !isEmpty(credentialsRegion)) {
                    base = base.replace(`{${variable.name}}`, credentialsRegion);
                    return;
                }

                base = base.replace(`{${variable.name}}`, `\${${variable.name}}`);
            });

            let path = apiPath.path;
            apiPath.pathParameters?.forEach(parameter => {
                path = path.replace(`{${parameter.name}}`, `\${${parameter.name}}`);
            });

            const requestURL = new URL(`${base?.replace(/\/+$/, '')?.replace(/[<\[]/, "${")?.replace(/[>\]]/, "}")}${path?.replace(/^\/+/, '/')}`);
            apiPath.queryParameters?.forEach(parameter => {
                if (parameter.required === true || !isEmpty(parameter.defaultValue)) {
                    requestURL.searchParams.append(parameter.name, isEmpty(parameter.defaultValue) ? `\${${parameter.name}}` : urlEncodedValue(parameter.defaultValue));
                }
            });

            let isFormUrlencoded = false;
            const headers = {};
            apiPath.headerParameters?.forEach(parameter => {
                if (parameter.name === 'Content-Type' && parameter.defaultValue === 'application/x-www-form-urlencoded') {
                    isFormUrlencoded = true;
                }

                if (parameter.required === true || !isEmpty(parameter.defaultValue)) {
                    headers[parameter.name] = isEmpty(parameter.defaultValue) ? `\${${parameter.name}}` : parameter.defaultValue;
                }
            });

            const url = window.decodeURIComponent(requestURL.href);

            cell.openApiSlug = slug;
            task.httpRequest = {
                url,
                method: apiPath.method,
                headers,
                body: isFormUrlencoded ? window.decodeURIComponent($.param(apiPath.body)) : apiPath.body
            };
            httpTaskForm.setHttpRequest(task.httpRequest);
            updateCellValue(model, cell, {httpRequest: task.httpRequest});
        },
        apiSettings: {
            url: '/api/search/open-apis?query={query}&size=100',
            onResponse: ({content}) => {
                const response = {results: {}};
                $.each(content, function (index, item) {
                    const name = item.name || 'Unknown';
                    const maxResults = 10;

                    if (index >= maxResults) {
                        return false;
                    }

                    if (response.results[name] === undefined) {
                        response.results[name] = {
                            name,
                            results: [],
                        };
                    }

                    response.results[name].results.push({
                        title: `${item.apiPath.method} ${item.apiPath.path}`,
                        description: item.apiPath.summary,
                        image: `/images/logos/${item.logo}` || '',
                        apiPath: item.apiPath,
                        servers: item.servers,
                        slug: item.slug
                    });
                });
                return response;
            },
        },
    });

    if (!isEmpty(cell.openApiSlug)) {
        api(`/integration-operation/${cell.openApiSlug}`, {
            method: 'GET',
            headers: {
                'Accept': 'text/html'
            }
        })
            .then(pageContent => {
                import(/* webpackPrefetch: true */ 'highlight.js/styles/idea.css');
                document.querySelector('#httpRequestEditor #openApisSearch')?.insertAdjacentHTML('beforeend', pageContent);
            })
            .catch(error => LOGGER.error("Unable to load integration page", error));
    }

    document.getElementById('httpRequestEditor').insertAdjacentElement(
        'afterend',
        createButtonField({
            id: 'testHttpRequestField',
            buttonId: 'testHttpRequest',
            label: mxResources.get('testRequest')
        })
    );
    document.getElementById('testHttpRequest')?.addEventListener('click', e => {
        const button = e.currentTarget;
        document.getElementById('testHttpResponse')?.remove();
        $(button).api('destroy');

        if (!httpTaskForm.isValid() || isEmpty(task.httpRequest?.url)) {
            e.preventDefault();
            return;
        }

        const credentials = task.credentials;
        const {url, method, headers, body} = task.httpRequest || {};
        const isSimWorkflowApi = url?.startsWith('/api/');

        async function fetchOAuth2AuthorizeRequired() {
            if (!isEmpty(credentials)) {
                return await api(`/api/credentials/${credentials}`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json'
                    }
                })
                    .then(response => response?.credentials?.type === 'OAUTH2' && response?.authorized !== true)
                    .catch(() => false);
            }

            return false;
        }

        setTestButtonLoading(button, true);
        fetchOAuth2AuthorizeRequired().then(oAuth2AuthorizeRequired => {
            if (oAuth2AuthorizeRequired) {
                setTestButtonLoading(button, false);
                e.preventDefault();
                e.stopPropagation();
                window.open(`/credentials/${credentials}/authorize`, '_blank').focus();
                return false;
            }

            setTestButtonLoading(button, false);
            $(button).api({
                cache: !isSimWorkflowApi,
                url: isSimWorkflowApi ? url : '/api/proxy',
                method: isSimWorkflowApi ? method : 'POST',
                beforeXHR: function (xhr, settings) {
                    if (isSimWorkflowApi) {
                        settings.data = body;

                        if (!isEmpty(headers)) {
                            Object.keys(headers).forEach(headerName => {
                                xhr.setRequestHeader(headerName,
                                    headers[headerName]);
                            });
                        }
                    } else {
                        settings.data = stringifyJSON({
                            credentials,
                            url: urlData(url, this),
                            method,
                            headers,
                            body,
                            input: task.inputParameters
                        });
                    }
                    xhr.setRequestHeader('Content-Type', 'application/json');
                    xhr.setRequestHeader('Accept', 'application/json');
                    return xhr;
                },
                onComplete: function (response, element, xhr) {
                    setTestButtonLoading(button, false);
                    const {status, statusText} = xhr;

                    renderTestResponseField({
                        id: 'testHttpResponse',
                        label: mxResources.get('httpResponse', [status, statusText]),
                        error: !(status === 0 || (status >= 200 && status < 400)),
                        response,
                        afterElement: document.getElementById('testHttpRequestField')
                    });
                }
            }).api('query');
            setTestButtonLoading(button, true);
        });
    });
}

const handleAIChatTab = (container, editable, sidebar, model, cell, task, taskErrors) => {
    document.querySelectorAll('.test-ai-chat-field').forEach(field => field.remove());
    document.getElementById('testAIChatResponse')?.remove();
    let aiChatTaskForm = null;

    const aiChatTestButtons = () => [...container.querySelectorAll('.test-ai-chat-button')];

    const setAIChatTestButtonsLoading = loading =>
        aiChatTestButtons().forEach(testButton => setTestButtonLoading(testButton, loading));

    const responseAnchorForField = field => field?.closest('.ai-chat-message-actions, .mcp-servers-actions') || field;

    const applyAIChatChange = aiChat => {
        applyAIChatToTask(task, aiChat);
        model.setStyle(cell, mxUtils.setStyle(model.getStyle(cell), 'image', getProviderLogo(task.chatModel?.provider)));
        model.setValue(cell, applyAIChatToTask(deepCopy(cell.getValue()), aiChat));
    };

    const testAIChat = e => {
        let button = e.currentTarget;
        if (button.disabled) {
            e.preventDefault();
            return;
        }
        let field = button.closest('.test-ai-chat-field');
        const placement = field?.dataset.testAiChatPlacement;

        document.getElementById('testAIChatResponse')?.remove();
        aiChatTestButtons().forEach(testButton => $(testButton).api('destroy'));

        if ((isEmpty(task?.userMessages) && isEmpty(task?.systemMessages)) ||
            isEmpty(task.chatModel?.credentials) || isEmpty(task.chatModel?.provider) ||
            !aiProviders.hasOwnProperty(task.chatModel?.provider) || isEmpty(task.chatModel?.model) ||
            !aiChatTaskForm?.isValid?.()) {
            e.preventDefault();
            return;
        }

        const placementId = testAIChatPlacementIds[placement] || 'Task';
        button = document.getElementById(`testAIChat${placementId}`) || button;
        field = button.closest('.test-ai-chat-field') || field;
        const {userMessages, systemMessages, chatModel, mcpServers} = task;
        const responseAnchor = responseAnchorForField(field);

        $(button).api({
            url: '/api/ai-chat-test',
            contentType: 'application/json',
            processData: false,
            method: 'POST',
            data: stringifyJSON({
                userMessages,
                systemMessages,
                chatModel,
                mcpServers
            }),
            beforeXHR: function (xhr) {
                xhr.setRequestHeader('Content-Type', 'application/json');
                return xhr;
            },
            onComplete: function (response, element, xhr) {
                setAIChatTestButtonsLoading(false);
                renderTestResponseField({
                    id: 'testAIChatResponse',
                    label: mxResources.get('aiChatResponse', [xhr.status, xhr.statusText]),
                    response,
                    afterElement: responseAnchor
                });
            }
        }).api('query');
        setAIChatTestButtonsLoading(true);
    };

    const createTestAIChatField = placement => {
        const placementId = testAIChatPlacementIds[placement] || 'Task';
        const field = createButtonField({
            id: `testAIChat${placementId}Field`,
            buttonId: `testAIChat${placementId}`,
            label: mxResources.get('testAIChat')
        });
        field.className += ' test-ai-chat-field';
        field.dataset.testAiChatPlacement = placement;
        const button = field.querySelector('button');
        button.className += ' test-ai-chat-button';
        button.addEventListener('click', testAIChat);
        return field;
    };

    const messagesEditor = document.getElementById('aiChatMessagesEditor');
    const modelEditor = document.getElementById('aiChatModelEditor');
    const mcpServersEditor = document.getElementById('aiChatMCPServersEditor');

    aiChatTaskForm = createAIChatTaskForm({
        task,
        taskErrors,
        editable,
        mxResources,
        messagesMountElement: messagesEditor,
        chatModelMountElement: modelEditor,
        mcpServersMountElement: mcpServersEditor,
        activeMessageTabId: container.activeAIChatMessageTab,
        createTestAIChatField,
        onAIChatChange: applyAIChatChange,
        onRender: () => setTimeout(() => {
            initializeAIChatDropdowns(container);
            if (container.aiChatTaskForm) {
                initializeAIChatMessageTabs(container);
                loadJsonExpressionHelp(jsonExpressionHelp =>
                    container.aiChatTaskForm?.appendJsonExpressionHelp?.(jsonExpressionHelp), container);
            }
        }),
        onProviderChange: provider => {
            model.setStyle(cell, mxUtils.setStyle(model.getStyle(cell), 'image', getProviderLogo(provider)));
        }
    });

    messagesEditor.dataset.aiChatMounted = 'true';
    container.aiChatTaskForm = aiChatTaskForm;

    loadJsonExpressionHelp(jsonExpressionHelp =>
        container.aiChatTaskForm?.appendJsonExpressionHelp?.(jsonExpressionHelp), container);

    initializeAIChatDropdowns(container);
    initializeAIChatMessageTabs(container);
}

const initializeAIChatMessageTabs = container => {
    $(document.getElementById('aiChatMessagesEditor')?.querySelectorAll('.ai-chat-message-tab-menu .item') || []).tab({
        duration: 0,
        onVisible: tabPath => {
            container.activeAIChatMessageTab = tabPath;
            container.aiChatTaskForm?.setActiveMessageTab?.(tabPath);
            refreshActiveTabEditor(container, 'taskTabAIChatMessages');
        }
    });
}

const handleAIChatModelTab = container => {
    const responseJSONSchemaEditor = document.getElementById('chatModelResponseJSONSchemaEditor');
    if (!responseJSONSchemaEditor || responseJSONSchemaEditor.dataset.editorLoading === 'true') {
        return;
    }

    responseJSONSchemaEditor.dataset.editorLoading = 'true';
    import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor')
        .then(module => container.aiChatTaskForm?.mountResponseJSONSchemaEditor(module))
        .catch(() => unableToLoadEditor('chatModelResponseJSONSchemaEditor'));
}

const handleEmailTab = (container, editable, sidebar, model, cell, task, taskErrors) => {
    container.emailTaskForm?.destroy?.();

    const emailTaskForm = createEmailTaskForm({
        task,
        taskErrors,
        editable,
        mxResources,
        activeBodyTabId: container.activeEmailBodyTab,
        onEmailChange: email => {
            task.email = email;
            updateCellValue(model, cell, {email: task.email});
        }
    });

    document.getElementById('emailEditor').replaceWith(emailTaskForm.element);
    container.emailTaskForm = emailTaskForm;
    loadJsonExpressionHelp(jsonExpressionHelp =>
        container.emailTaskForm?.appendJsonExpressionHelp?.(jsonExpressionHelp), container);
    if (editable) {
        initializeEmailDropdowns(container);
    }
    $(document.getElementById('emailEditor')?.querySelectorAll('.email-body-tab-menu .item') || []).tab({
        duration: 0,
        onVisible: tabPath => {
            container.activeEmailBodyTab = tabPath;
            container.emailTaskForm?.setActiveBodyTab?.(tabPath);
            refreshActiveTabEditor(container, 'taskTabEmail');
        }
    });

}

const handleRoutingTab = (
    container, editable, sidebar, model, cell, route, routeErrors) => {
    const editorElement = getLazyEditorMountElement('routingConditionEditor');
    if (!editorElement) {
        return;
    }

    import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor')
        .then(module => module.codeEditor({
            callbackFn: editor => {
                if (!isCurrentLazyEditorMount('routingConditionEditor', editorElement)) {
                    editor.getWrapperElement()?.remove?.();
                    return;
                }

                editor.on('blur', codeMirror => {
                    const getAnnotations = codeMirror.getHelper(module.CodeMirror.Pos(0, 0), 'lint');
                    const annotations = getAnnotations(codeMirror.getValue(), codeMirror.state?.lint?.linterOptions, codeMirror);

                    const oldValue = route.condition;
                    const newValue = codeMirror.getValue().trim();
                    if (!isEqual(oldValue, newValue) && isEmpty(annotations)) {
                        route.condition = codeMirror.getValue().trim();
                        updateCellValue(model, cell, {condition: route.condition});
                    }
                });

                const fieldElement = editor.getWrapperElement();
                fieldElement.classList.add('ui', 'segment');

                editorElement.replaceWith(mountedEditorField(sidebar.createField({
                    id: 'routingConditionEditor',
                    error: routeErrors?.fields?.condition,
                    fieldElement
                })));

                const conditionField = document.getElementById('routingConditionEditor');
                const helpCleanups = sidebar.container.inputParametersHelpCleanups ||= new Set();
                import(/* webpackChunkName: "conditionExpressionHelp" */ "./help/conditionExpressionHelp")
                    .then(module => {
                        if (!conditionField.isConnected || sidebar.container.inputParametersHelpCleanups !== helpCleanups) {
                            return;
                        }
                        if (!conditionField.querySelector(':scope > details')) {
                            conditionField.insertAdjacentHTML('beforeend', module.conditionExpressionHelp);
                            helpCleanups.add(module.initializeConditionExpressionHelp(conditionField.querySelector(':scope > details')));
                        }
                    })
                    .catch(error => logUnableToLoadHelpError(error));

                editor.refresh();
            },
            errorFn: () => unableToLoadCurrentEditor('routingConditionEditor', editorElement),
            options: {
                readOnly: !editable ? 'nocursor' : false,
                mode: 'javascript',
                value: route.condition || '',
                lint: {
                    asi: true,
                    expr: true,
                    esversion: 6,
                },
                hintOptions: {hint: javascriptHint},
                extraKeys: {'Ctrl-Space': 'autocomplete'},
            }
        })).catch(() => unableToLoadCurrentEditor('routingConditionEditor', editorElement));
}

/**
 * Constructs a new sidebar for the given editor.
 */
export const Sidebar = function (editorUi, container, currentWorkflowDefinition) {
    this.editorUi = editorUi;
    this.container = container;
    this.activeTab = null;
    this.currentWorkflowDefinition = currentWorkflowDefinition;
    this.vectorStoreDrafts = [];
    this.vectorDraftBaselineModified = null;
    this.vectorDraftModelChanged = false;
    container.credentialsCacheNamespace = uuid();
    editorUi.destroyFunctions?.push(() => {
        container.querySelectorAll('select').forEach(select => select.destroyCredentialsDropdown?.());
    });

    this.init();
};

Sidebar.prototype.init = function () {
    const graph = this.editorUi.editor.graph;

    this.update = mxUtils.bind(this, function (sender, evt) {
        if (sender === graph.getModel() && this.vectorStoreDrafts.length) {
            this.vectorDraftModelChanged = true;
        }
        if (evt.getName() === mxEvent.EDITING_STARTED && this.invalidResourceNames) {
            return;
        }
        this.refresh();
    });

    graph.getSelectionModel().addListener(mxEvent.CHANGE, this.update);
    graph.addListener(mxEvent.EDITING_STARTED, this.update);
    graph.addListener(mxEvent.EDITING_STOPPED, this.update);
    graph.getModel().addListener(mxEvent.CHANGE, this.update);
    graph.addListener(mxEvent.ROOT, this.update);
};

Sidebar.prototype.clear = function () {
    this.container.inputParametersHelpCleanups?.forEach(cleanup => cleanup());
    delete this.container.inputParametersHelpCleanups;
    this.container.querySelectorAll('select').forEach(select => select.destroyCredentialsDropdown?.());
    this.invalidResourceNames = false;
    this.workflowResourcesForm = null;
    this.container.aiChatTaskForm?.destroy?.();
    delete this.container.aiChatTaskForm;
    this.container.aiVectorStoreTaskForm?.destroy?.();
    delete this.container.aiVectorStoreTaskForm;
    this.container.documentReaderTaskForm?.destroy?.();
    delete this.container.documentReaderTaskForm;
    this.container.jsonConverterTaskForm?.destroy?.();
    delete this.container.jsonConverterTaskForm;
    this.container.emailTaskForm?.destroy?.();
    delete this.container.emailTaskForm;
    this.container.innerHTML = '';

    if (this.panels != null) {
        for (let i = 0; i < this.panels.length; i++) {
            this.panels[i].destroy();
        }
    }

    this.panels = [];
};

Sidebar.prototype.refresh = function () {
    if (this.container.style.width === '0px') {
        return;
    }

    this.clear();

    const graph = this.editorUi.editor.graph;

    if (graph.getSelectionCount() > 1) {
        return;
    }

    const containerFragment = document.createDocumentFragment();
    const tabsContainer = document.createElement('div');
    tabsContainer.className = 'ui top attached tabular stackable menu';
    containerFragment.appendChild(tabsContainer);

    if (graph.isSelectionEmpty()) {
        if (!this.activeTab?.startsWith('workflow')) {
            this.activeTab = null;
        }
        this.createWorkflowGeneralTab(tabsContainer);
        this.createWorkflowStartFormTab(tabsContainer);
        this.createWorkflowErrorHandlingTab(tabsContainer);

        if (!isEmpty(this.currentWorkflowDefinition.id)) {
            this.createWorkflowVersionsTab(tabsContainer);
        }
        this.createWorkflowResourcesTab(tabsContainer);
    } else {
        const cell = graph.getSelectionCell();
        if (graph.getModel().isEdge(cell)) {
            if (!this.activeTab?.startsWith('routing')) {
                this.activeTab = null;
            }
            this.createRoutingPropertiesTab(tabsContainer);
        } else {
            if (!this.activeTab?.startsWith('task')) {
                this.activeTab = null;
            }
            this.createTaskGeneralTab(tabsContainer);

            const outgoingEdges = graph.getModel().getEdges(cell, false, true, true);
            if (!cell.isStart() || (cell.isStart() && outgoingEdges.length > 1)) {
                if (!cell.isStart() && cellSupportsInputParameterControls(cell)) {
                    this.createTaskInputParametersTab(tabsContainer);
                }

                if (cell.isEnd()) {
                    this.createTaskWorkflowOutput(tabsContainer);
                }
                if (cell.isSimple()) {
                    this.createTaskProcessorTab(tabsContainer);
                }
                if (cell.isHttp()) {
                    this.createTaskHttpTab(tabsContainer);
                }
                if (cell.isAIChat()) {
                    this.createAIChatTab(tabsContainer);
                }
                if (['AIVectorStore', 'AIVectorSearch', 'AIVectorDelete'].includes(cell.getValue()?.type)) {
                    this.createAIVectorStoreTab(tabsContainer);
                }
                if (cell.isDocumentReader()) {
                    this.createDocumentReaderTab(tabsContainer);
                }
                if (cell.isJsonConverter()) {
                    this.createJsonConverterTab(tabsContainer);
                }
                if (cell.isEmail()) {
                    this.createEmailTab(tabsContainer);
                }
                if (cell.isJQ()) {
                    this.createTaskJQTab(tabsContainer);
                }
                if (cell.isMap()) {
                    this.createTaskMapTab(tabsContainer);
                }
                if (cell.isWait()) {
                    this.createTaskWaitTab(tabsContainer);
                }
                if (cellSupportsRetryControls(cell) && !(cell.isStart() && cell.isSplit())) {
                    this.createErrorHandlingTab(tabsContainer);
                }

                this.createTaskRoutingTab(tabsContainer);
            }
        }
    }

    this.container.appendChild(containerFragment);

    const sidebar = this;
    $(this.container.querySelectorAll('.menu .item')).tab({
        autoTabActivation: sidebar.activeTab || true,
        onVisible: tabPath => {
            sidebar.activeTab = tabPath;
            removeInactivePagePreviews(this.container, tabPath);

            const model = graph.getModel();
            const workflow = model.getRoot().getValue();
            const cell = graph.getSelectionCell();

            if (tabPath === 'workflowErrorHandling' && document.getElementById('failureWorkflowDiagram')) {
                handleWorkflowErrorHandlingTab(this.container, model, workflow, this.currentWorkflowDefinition);
            } else if (tabPath === 'workflowStartForm') {
                handleWorkflowStartFormTab(this.container, model, this.currentWorkflowDefinition);
            } else if (tabPath === 'taskProcessor') {
                handleTaskProcessorTab(this.container, model, cell, deepCopy(cell.getValue()), this.currentWorkflowDefinition);
            } else if (tabPath === 'taskTabWorkflowOutput') {
                handleWorkflowOutputTab(this.container, this.editorUi.editor.editable, this, model, cell, deepCopy(cell.getValue()), graph.getTaskErrors(cell.id));
            } else if (tabPath === 'taskTabInputParameters') {
                handleInputParametersTab(
                    this.container,
                    this.editorUi.editor.editable,
                    this,
                    model,
                    cell,
                    deepCopy(cell.getValue()),
                    graph.getTaskErrors(cell.id),
                    null,
                    'inputParametersEditor');
            } else if (tabPath === 'taskRouting') {
                const taskValuePath = transitionInputParametersTaskValuePath(cell, model);
                if (cellSupportsRoutingParameters(cell, model) && document.getElementById('outgoingRoutingInputParametersEditor')) {
                    handleInputParametersTab(
                        this.container,
                        this.editorUi.editor.editable,
                        this,
                        model,
                        cell,
                        deepCopy(cell.getValue()),
                        graph.getTaskErrors(cell.id),
                        taskValuePath,
                        'outgoingRoutingInputParametersEditor',
                        mxResources.get('routingParameters'),
                        mxResources.get('routingParameters'));
                }
            } else if (tabPath === 'taskTabHttp' && document.getElementById('httpRequestEditor')) {
                handleHttpTab(this.container, this.editorUi.editor.editable, this, model, cell, deepCopy(cell.getValue()), graph.getTaskErrors(cell.id));
            } else if (isAIChatTabPath(tabPath) && !this.container.aiChatTaskForm && document.getElementById('aiChatMessagesEditor')) {
                handleAIChatTab(this.container, this.editorUi.editor.editable, this, model, cell, deepCopy(cell.getValue()), graph.getTaskErrors(cell.id));
                if (tabPath === 'taskTabAIChatModel') {
                    handleAIChatModelTab(this.container);
                }
            } else if (tabPath === 'taskTabAIChatModel') {
                handleAIChatModelTab(this.container);
            } else if (tabPath === 'workflowResources' || tabPath === 'taskTabAIVectorStore') {
                initializeCredentialsDropdowns(this.container);
                if (tabPath === 'workflowResources') {
                    initializeAIEmbeddingModelDropdowns(this.container);
                }
            } else if (tabPath === 'taskTabEmail' && document.getElementById('emailEditor') && !this.container.emailTaskForm) {
                handleEmailTab(this.container, this.editorUi.editor.editable, this, model, cell, deepCopy(cell.getValue()), graph.getTaskErrors(cell.id));
            } else if (tabPath === 'routingProperties') {
                handleRoutingTab(
                    this.container,
                    this.editorUi.editor.editable,
                    this,
                    model,
                    cell,
                    deepCopy(cell.getValue()),
                    graph.getRouteErrors(cell.id));
            } else if (tabPath === 'workflowVersions') {
                this.fetchVersions();
            }

            refreshActiveTabEditor(this.container, tabPath, activeTabPath => sidebar.activeTab === activeTabPath);
        }
    });
    if (this.editorUi.editor.editable) {
        initializeBasicDropdowns(
            this.container,
            'select.ui.dropdown:not(.credentials,.failureWorkflow,.failureWorkflowVersion,.processorPage,.processorPageVersion,.workflowStartPage,.workflowStartPageVersion)'
        );
        dataCopyValue(this.container);
    } else {
        this.container.querySelectorAll('input, textarea, select, button, .ui.dropdown, .ui.button').forEach(element => {
            element.disabled = true;
            element.setAttribute('disabled', '');
        });
    }
};

Sidebar.prototype.createWorkflowGeneralTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const cell = model.getRoot();
    const workflow = deepCopy(cell.getValue());
    const workflowErrors = graph.getWorkflowErrors();

    const workflowNameField = this.createInputField({
        label: mxResources.get('name'),
        id: 'name',
        name: 'name',
        type: 'text',
        className: 'required',
        value: workflow.name || '',
        error: workflowErrors?.fields?.name
    });

    mxEvent.addListener(workflowNameField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        workflow.name = evt.target.value;
        updateRootValue(model, {name: workflow.name});
    }));

    const workflowDescriptionField = this.createTextareaField({
        label: mxResources.get('description'),
        id: 'description',
        name: 'description',
        rows: 2,
        value: workflow.description || ''
    });
    mxEvent.addListener(workflowDescriptionField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        workflow.description = evt.target.value;
        updateRootValue(model, {description: workflow.description});
    }));

    const workflowTimeoutSecondsField = this.createInputField({
        label: mxResources.get('workflowTimeoutSeconds'),
        help: mxResources.get('workflowTimeoutSecondsHelp'),
        id: 'workflowTimeoutSeconds',
        name: 'workflowTimeoutSeconds',
        type: 'number',
        min: 0,
        value: workflow.timeoutSeconds || 0,
        placeHolder: 0,
        error: workflowErrors?.fields?.timeoutSeconds
    });
    mxEvent.addListener(workflowTimeoutSecondsField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        workflow.timeoutSeconds = evt.target.value;
        updateRootValue(model, {timeoutSeconds: workflow.timeoutSeconds});
    }));

    const workflowMaxConcurrencyField = this.createInputField({
        label: mxResources.get('workflowMaxConcurrency'),
        help: mxResources.get('workflowMaxConcurrencyHelp'),
        id: 'workflowMaxConcurrency',
        name: 'workflowMaxConcurrency',
        type: 'number',
        min: 0,
        value: workflow.maxConcurrency || 0,
        placeHolder: 0,
        error: workflowErrors?.fields?.maxConcurrency
    });
    mxEvent.addListener(workflowMaxConcurrencyField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        workflow.maxConcurrency = evt.target.value;
        updateRootValue(model, {maxConcurrency: workflow.maxConcurrency});
    }));

    const fieldErrors = [];
    workflowErrors?.fields?.name && fieldErrors.push(workflowErrors.fields.name);
    workflowErrors?.fields?.timeoutSeconds && fieldErrors.push(workflowErrors.fields.timeoutSeconds);
    workflowErrors?.fields?.maxConcurrency && fieldErrors.push(workflowErrors.fields.maxConcurrency);

    const objectErrors = Array.isArray(workflowErrors)
        ? workflowErrors.map(error => error.message)
        : collectFieldErrors(workflowErrors?.message);

    const workflowProperties = this.createTab(tabsContainer, 'workflowGeneral', mxResources.get('workflowTabGeneral'), objectErrors, fieldErrors);
    workflowProperties.appendChild(workflowNameField);
    workflowProperties.appendChild(workflowDescriptionField);
    workflowProperties.appendChild(workflowTimeoutSecondsField);
    workflowProperties.appendChild(workflowMaxConcurrencyField);
};

Sidebar.prototype.createWorkflowResourcesTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const resources = deepCopy(model.getRoot().getValue().resources || {});
    const workflowErrors = graph.getWorkflowErrors();
    const tab = this.createTab(tabsContainer, 'workflowResources', mxResources.get('workflowTabResources'), [],
        collectFieldErrors(workflowErrors?.fields?.resources));
    const form = createVectorStoreResourcesForm({
        resources, errors: workflowErrors?.fields?.resources, drafts: this.vectorStoreDrafts,
        editable: this.editorUi.editor.editable, mxResources,
        managedVectorAllowed: this.editorUi.container?.closest('.graph-content')
            ?.dataset.managedVectorAllowed === 'true',
        onRender: () => {
            initializeCredentialsDropdowns(this.container);
            initializeBasicDropdowns(this.container, '#workflowVectorStoresEditor select.ui.dropdown:not(.credentials)');
            initializeAIEmbeddingModelDropdowns(this.container);
        },
        onChange: value => {
            model.beginUpdate();
            try {
                updateRootValue(model, {resources: value});
                const pending = [model.getRoot()];
                while (pending.length > 0) {
                    const cell = pending.pop();
                    const image = vectorTaskImage(cell.getValue?.(), value);
                    if (image) {
                        model.setStyle(cell, mxUtils.setStyle(model.getStyle(cell), 'image', image));
                    }
                    pending.push(...(cell.children || []));
                }
            } finally {
                model.endUpdate();
            }
        },
        onDraftAdded: () => {
            if (this.vectorDraftBaselineModified === null) {
                this.vectorDraftBaselineModified = this.editorUi.editor.modified;
                this.vectorDraftModelChanged = false;
            }
            this.editorUi.editor.setModified(true);
        },
        onDraftRemoved: materialized => {
            if (!this.vectorStoreDrafts.length) {
                if (!materialized && !this.vectorDraftBaselineModified && !this.vectorDraftModelChanged) {
                    this.editorUi.editor.setModified(false);
                }
                this.vectorDraftBaselineModified = null;
                this.vectorDraftModelChanged = false;
            }
        }
    });
    this.workflowResourcesForm = form;
    tab.appendChild(form.element);
};

Sidebar.prototype.validatePendingResourceNames = function () {
    this.container.aiChatTaskForm?.sync?.();
    this.container.aiVectorStoreTaskForm?.sync?.();
    this.container.documentReaderTaskForm?.sync?.();
    this.container.jsonConverterTaskForm?.sync?.();
    if (!this.workflowResourcesForm && this.vectorStoreDrafts.length) {
        this.editorUi.editor.graph.clearSelection();
        this.activeTab = 'workflowResources';
        this.refresh();
    }
    const valid = this.workflowResourcesForm?.validateNames() ?? true;
    this.invalidResourceNames = !valid;
    return valid;
};

Sidebar.prototype.createWorkflowStartFormTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const workflowStartPage = getWorkflowStartPage(model);
    const workflowStartPageId = pageReferencePageId(workflowStartPage);
    const workflowStartPageVersion = pageReferenceVersion(workflowStartPage);
    const templateLaunchPage = this.currentWorkflowDefinition.templateLaunchPage;
    const hasTemplateLaunchPage = !isEmpty(templateLaunchPage);

    const workflowStartForm = this.createTab(tabsContainer, 'workflowStartForm', mxResources.get('workflowTabStartForm'));
    workflowStartForm.insertAdjacentHTML('beforeend', `<p>${mxResources.get('workflowStartPageHelp')}</p>`);

    const pageDropdownField = this.createDropdown({
        label: mxResources.get('page'),
        id: 'workflowStartPage',
        name: 'workflowStartPage',
        value: templateLaunchPage ? templateLaunchPage.title : workflowStartPageId,
        clearable: true,
        searchable: true,
        disabled: hasTemplateLaunchPage,
        options: templateLaunchPage ? [{
            value: templateLaunchPage.title,
            name: templateLaunchPage.title
        }] : [{
            value: '',
            name: mxResources.get('workflowStartPageSelect')
        }]
    });
    const pageVersionDropdownField = this.createDropdown({
        label: mxResources.get('pageVersion'),
        id: 'workflowStartPageVersion',
        name: 'workflowStartPageVersion',
        className: 'workflowStartPageVersion',
        value: templateLaunchPage ? String(templateLaunchPage.pageVersion) : workflowStartPageVersion,
        clearable: true,
        searchable: true,
        disabled: hasTemplateLaunchPage || isEmpty(workflowStartPageId),
        options: templateLaunchPage ? [{
            value: String(templateLaunchPage.pageVersion),
            name: `Version ${templateLaunchPage.pageVersion}`
        }] : [{
            value: '',
            name: mxResources.get('latestPageVersion')
        }]
    });

    workflowStartForm.appendChild(pageDropdownField);
    workflowStartForm.appendChild(pageVersionDropdownField);
    if (templateLaunchPage) {
        const message = document.createElement('div');
        message.className = 'ui info visible message';
        message.textContent = `This template will copy the published Page ${templateLaunchPage.title} when you first save.`;
        workflowStartForm.appendChild(message);
    }
    appendPagePreview(workflowStartForm, workflowStartPage);
};

Sidebar.prototype.createWorkflowErrorHandlingTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const cell = model.getRoot();
    const workflow = deepCopy(cell.getValue());
    const workflowErrors = graph.getWorkflowErrors();

    const fieldErrors = [];
    workflowErrors?.fields?.failureWorkflow && fieldErrors.push(workflowErrors.fields.failureWorkflow);
    workflowErrors?.fields?.failureWorkflowVersion && fieldErrors.push(workflowErrors.fields.failureWorkflowVersion);

    const workflowProperties = this.createTab(tabsContainer, 'workflowErrorHandling', mxResources.get('workflowTabErrorHandling'), [], fieldErrors);
    workflowProperties.insertAdjacentHTML('beforeend', `<p>${mxResources.get('workflowTabErrorHandlingHelp')}</p>`);

    const failureWorkflow = workflow.failureWorkflow || '';
    const failureWorkflowDropdownField = this.createDropdown({
        id: 'failureWorkflow',
        name: 'failureWorkflow',
        label: mxResources.get('failureWorkflow'),
        value: failureWorkflow,
        error: workflowErrors?.fields?.failureWorkflow,
        clearable: true,
        searchable: true,
        options: [{
            value: '',
            name: mxResources.get('failureWorkflowSelect')
        }]
    });

    const failureWorkflowVersion = workflow.failureWorkflowVersion || '';
    const failureWorkflowVersionDropdownField = this.createDropdown({
        id: 'failureWorkflowVersion',
        name: 'failureWorkflowVersion',
        label: mxResources.get('failureWorkflowVersion'),
        value: failureWorkflowVersion,
        error: workflowErrors?.fields?.failureWorkflowVersion,
        clearable: true,
        searchable: true,
        disabled: isEmpty(failureWorkflow),
        options: [{
            value: '',
            name: mxResources.get('failureWorkflowVersionSelect')
        }]
    });

    workflowProperties.appendChild(failureWorkflowDropdownField);
    workflowProperties.appendChild(failureWorkflowVersionDropdownField);

    workflowProperties.insertAdjacentHTML('beforeend', `
    <div id="failureWorkflowDiagram" class="ui segments graph-content" style="display: none"></div>
    <p>When this workflow fails, its configured failure workflow starts with the original workflow input.</p>
    <p>The input also includes <code>swf:failureWorkflow</code>, describing the failure. For example:</p>
    `);
    workflowProperties.appendChild(createTestResponseField({
        id: 'failureWorkflowInputExample',
        label: 'Example input',
        response: {
            requestId: 'original input value',
            'swf:failureWorkflow': {
                'swf:workflowId': 'failed workflow ID',
                'swf:workflowStatus': 'Failed',
                'swf:workflowDefinitionId': 'workflow definition ID',
                'swf:workflowDefinitionRevision': 1,
                'swf:workflowDefinitionVersion': null,
                'swf:taskId': null,
                'swf:taskStatus': null,
                'swf:cause': 'failure reason'
            }
        }
    }));
    workflowProperties.insertAdjacentHTML('beforeend', '<p>Task fields may be null when the failure is not tied to a specific task. The definition version may be null for a draft.</p>');
};

Sidebar.prototype.createWorkflowVersionsTab = function (tabsContainer) {
    const workflowVersions = this.createTab(tabsContainer, 'workflowVersions', mxResources.get('workflowTabVersions'));
    workflowVersions.insertAdjacentHTML('beforeend', `
      <p>A version is an immutable snapshot of your workflow definition. When you point your application at a workflow definition version, it will continue to use that version even after you update the workflow definition.</p>
    `);
    workflowVersions.appendChild(this.createVersions());
};

Sidebar.prototype.createVersions = function () {
    const versionsContainer = document.createElement('table');
    versionsContainer.className = 'ui striped compact table';
    versionsContainer.insertAdjacentHTML('beforeend', `<thead><th class="collapsing right aligned">${mxResources.get('version')}</th><th>${mxResources.get('enabled')}</th><th>${mxResources.get('updatedOn')}</th><th class="collapsing"></th></tr></thead>`);

    const tableBody = document.createElement('tbody');
    tableBody.insertAdjacentHTML('beforeend', `<tr><td colspan="3"><div class="ui active centered inline loader"></div></td></tr>`);
    versionsContainer.appendChild(tableBody);

    return versionsContainer;
}

Sidebar.prototype.fetchVersions = function () {
    const workflowDefinitionId = this.currentWorkflowDefinition.id;
    if (workflowDefinitionId && workflowDefinitionId.length > 0) {
        api(`/api/workflow-definitions/${workflowDefinitionId}/versions`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        }).then(workflowDefinitions => {
            const versionsBody = document.createElement('tbody');

            if (isEmpty(workflowDefinitions)) {
                versionsBody.insertAdjacentHTML('beforeend', `
                  <tr>
                      <td colspan="3">${mxResources.get('noVersions')}</td>
                  </tr>
              `);
            } else {
                workflowDefinitions.forEach((workflowDefinition, index) => {
                    versionsBody.insertAdjacentHTML('beforeend', `
                                <tr>
                                    <td class="collapsing right aligned"><a href="/workflow-definitions/${workflowDefinition.id}?version=${workflowDefinition.version}">${workflowDefinition.version}</a></td>
                                    <td>
                                      <div class="ui fitted toggle checkbox">
                                      <input type="checkbox" name="disabledVersions" value="${workflowDefinition.version}"${workflowDefinition.disabled === false ? ' checked="checked"' : ''}>
                                      <label></label>
                                      </div>
                                    </td>
                                    <td><relative-time datetime="${workflowDefinition.createdOn}">${new Intl.DateTimeFormat(
                        navigator.language || 'default', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                        }).format(new Date(workflowDefinition.createdOn))}</relative-time></td>
                                </tr>
                            `);
                });
            }

            document.querySelector('#workflowVersionsContainer tbody').replaceWith(versionsBody);
            document.querySelectorAll('#workflowVersionsContainer tbody input[name="disabledVersions"]').forEach(el => {
                el.addEventListener('click', e => {
                    const operation = e.target.checked ? 'enable' : 'disable';
                    api(`/api/workflow-definitions/${workflowDefinitionId}/${operation}?version=${e.target.value}`, {
                        method: 'PUT'
                    }).catch(() => {
                        e.target.checked = !e.target.checked;
                    });
                })
            });
        }).catch(() => {
            const tableData = document.createElement('td');
            tableData.colSpan = 4;
            tableData.className = 'ui text error';
            tableData.textContent = `${mxResources.get('errorFetchingVersions')} `;

            const refreshLink = document.createElement('a');
            refreshLink.href = '#'
            refreshLink.textContent = mxResources.get('pleaseTryAgain');
            refreshLink.addEventListener('click', event => {
                event.preventDefault();
                this.createVersions();
                this.fetchVersions();
            });

            tableData.appendChild(refreshLink);
            tableData.appendChild(document.createTextNode('.'));

            const tableRow = document.createElement('tr');
            tableRow.appendChild(tableData);

            const retryBody = document.createElement('tbody');
            retryBody.appendChild(tableRow);

            document.querySelector('#workflowVersionsContainer tbody').replaceWith(retryBody);
        });
    } else {
        const noVersionsBody = document.createElement('tbody');
        noVersionsBody.insertAdjacentHTML('beforeend', `<tr><td colspan="4">${mxResources.get('noVersions')}</td></tr>`);
        document.querySelector('#workflowVersionsContainer tbody').replaceWith(noVersionsBody);
    }
}

Sidebar.prototype.createRoutingPropertiesTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const cell = graph.getSelectionCell();
    const route = deepCopy(cell.getValue());
    const routeErrors = graph.getRouteErrors(cell.id);

    const fieldErrors = [];
    routeErrors?.fields?.routingType && fieldErrors.push(routeErrors.fields.routingType);
    routeErrors?.fields?.condition && fieldErrors.push(routeErrors.fields.condition);

    const objectErrors = [];
    routeErrors?.message && objectErrors.push(routeErrors?.message);

    const routingProperties = this.createTab(tabsContainer, 'routingProperties', mxResources.get('routingTabProperties'), objectErrors, fieldErrors);

    const outgoingEdges = graph.getModel().getEdges(cell.source, false, true, true);
    const showRoutingType = (!cell.source.isStart() || (cell.source.isStart() && outgoingEdges.length > 1)) &&
        !cell.source.isSplit() &&
        !cell.target.isJoin();
    const hasUnconditional = showRoutingType &&
        cell.source.edges.some(edge => edge.source.id === cell.source.id && edge.id !== cell.id && !edge.isConditional());
    const routingForm = createRoutingForm({
        route,
        routeErrors,
        editable: this.editorUi.editor.editable,
        mxResources,
        hasUnconditional,
        conditionalDisabled: outgoingEdges.length <= 1,
        showRoutingType,
        onRouteChange: patch => {
            updateCellValue(model, cell, patch);
            if (Object.prototype.hasOwnProperty.call(patch, 'routingType') ||
                Object.prototype.hasOwnProperty.call(patch, 'condition')) {
                graph.fireEvent(new mxEventObject(UPDATE_CELL_VALUE, 'cell', cell));
            }
        }
    });
    routingProperties.appendChild(routingForm.element);
};

Sidebar.prototype.createTaskGeneralTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const cell = graph.getSelectionCell();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = [];
    taskErrors?.fields?.name && fieldErrors.push(taskErrors.fields.name);
    taskErrors?.fields?.terminationStatus && fieldErrors.push(taskErrors.fields.terminationStatus);

    const objectErrors = [];
    const configurationTask = ['AIChat', 'JsonDocumentReader', 'TextDocumentReader', 'HtmlDocumentReader', 'MarkdownDocumentReader', 'YamlDocumentReader', 'JsonConverter', 'AIVectorStore', 'AIVectorSearch', 'AIVectorDelete'].includes(task.type);
    if (taskErrors?.message && (!configurationTask ||
        !collectFieldErrors(taskErrors.fields).includes(taskErrors.message))) {
        objectErrors.push(taskErrors.message);
    }
    taskErrors?.fields?.nextTask && objectErrors.push(taskErrors.fields.nextTask);
    taskErrors?.fields?.branches && objectErrors.push(taskErrors.fields.branches);
    taskErrors?.fields?.choices && objectErrors.push(taskErrors.fields.choices);

    const taskGeneral = this.createTab(tabsContainer, 'taskGeneral', mxResources.get('taskTabGeneral'), objectErrors, fieldErrors);

    const taskNameField = this.createInputField({
        label: mxResources.get('name'),
        id: 'name',
        name: 'name',
        type: 'text',
        className: 'required',
        value: task.name,
        error: taskErrors?.fields?.name
    });
    mxEvent.addListener(taskNameField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        task.name = evt.target.value;
        updateCellValue(model, cell, {name: task.name});
    }));
    taskGeneral.appendChild(taskNameField);

    const outgoingEdges = graph.getModel().getEdges(cell, false, true, true);
    if (!cell.isStart() || (cell.isStart() && outgoingEdges.length > 1)) {
        const taskDescriptionField = this.createTextareaField({
            label: mxResources.get('description'),
            id: 'description',
            name: 'description',
            rows: 2,
            value: task.description || ''
        });
        mxEvent.addListener(taskDescriptionField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
            task.description = evt.target.value;
            updateCellValue(model, cell, {description: task.description});
        }));
        taskGeneral.appendChild(taskDescriptionField);
    }

    if (cell.isEnd()) {
        const terminationStatus = task.terminationStatus;

        const terminationStatusFields = this.createRadioFields({
            label: mxResources.get('terminationStatus'),
            name: 'terminationStatus',
            className: 'required',
            value: terminationStatus,
            options: [{
                label: mxResources.get('terminationStatusCompleted'),
                className: 'inline',
                help: mxResources.get('terminationStatusCompletedHelp'),
                value: 'Completed',
                onChange: mxUtils.bind(this, function (evt) {
                    task.terminationStatus = 'Completed';
                    updateCellValue(model, cell, {terminationStatus: task.terminationStatus});
                })
            }, {
                label: mxResources.get('terminationStatusFailed'),
                className: 'inline',
                help: mxResources.get('terminationStatusFailedHelp'),
                value: 'Failed',
                onChange: mxUtils.bind(this, function (evt) {
                    task.terminationStatus = 'Failed';
                    updateCellValue(model, cell, {terminationStatus: task.terminationStatus});
                })
            }]
        });

        taskGeneral.appendChild(terminationStatusFields);
    }
};

Sidebar.prototype.createTaskInputParametersTab = function (
    tabsContainer,
    {
        tabId = 'taskTabInputParameters',
        tabName = mxResources.get('taskTabInputParameters'),
        taskValuePath = null,
        editorId = 'inputParametersEditor'
    } = {}
) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const taskErrors = graph.getTaskErrors(cell.id);
    const inputParametersErrors = taskValuePath ? taskErrors?.fields?.[taskValuePath] : taskErrors?.fields;

    const fieldErrors = [];
    inputParametersErrors?.inputParameters && fieldErrors.push(inputParametersErrors.inputParameters);

    const taskTabInputParameters = this.createTab(tabsContainer, tabId, tabName, [], fieldErrors);
    const inputParametersEditor = document.createElement('div');
    inputParametersEditor.id = editorId;
    inputParametersEditor.className = 'ui active centered inline loader';

    taskTabInputParameters.appendChild(inputParametersEditor);
};

Sidebar.prototype.createTaskHttpTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = prepareHttpTaskErrors(taskErrors);

    const taskTabHttp = this.createTab(tabsContainer, 'taskTabHttp', mxResources.get('taskTabHttp'), [], fieldErrors);

    const defaultOption = {
        value: '',
        name: mxResources.get('credentialsSelect')
    };

    const credentialsDropdownField = this.createDropdown({
        label: mxResources.get('credentials'),
        id: 'credentials',
        name: 'credentials',
        error: taskErrors?.fields?.credentials,
        value: task.credentials || '',
        clearable: true,
        searchable: true,
        options: task.credentials
            ? [defaultOption, {value: task.credentials, name: task.credentials}]
            : [defaultOption]
    });

    taskTabHttp.appendChild(credentialsDropdownField);

    const httpRequestEditor = document.createElement('div');
    httpRequestEditor.id = 'httpRequestEditor';
    httpRequestEditor.className = 'ui active centered inline loader';

    taskTabHttp.appendChild(httpRequestEditor);

    const defaultConnectionTimeoutSeconds = 5;
    const defaultReadTimeoutSeconds = 10;
    const connectionTimeoutSecondsField = this.createInputField({
        label: mxResources.get('connectionTimeoutSeconds'),
        id: 'connectionTimeoutSeconds',
        name: 'connectionTimeoutSeconds',
        type: 'number',
        min: 1,
        value: task.connectionTimeoutSeconds || defaultConnectionTimeoutSeconds,
        placeHolder: defaultConnectionTimeoutSeconds,
        error: taskErrors?.fields?.connectionTimeoutSeconds
    });
    mxEvent.addListener(connectionTimeoutSecondsField, mxEvent.CHANGE,
        mxUtils.bind(this, function (evt) {
            task.connectionTimeoutSeconds = evt.target.value;
            updateCellValue(model, cell, {connectionTimeoutSeconds: task.connectionTimeoutSeconds});
        }));
    taskTabHttp.appendChild(connectionTimeoutSecondsField);

    const readTimeoutSecondsField = this.createInputField({
        label: mxResources.get('readTimeoutSeconds'),
        id: 'readTimeoutSeconds',
        name: 'readTimeoutSeconds',
        type: 'number',
        min: 1,
        value: task.readTimeoutSeconds || defaultReadTimeoutSeconds,
        placeHolder: defaultReadTimeoutSeconds,
        error: taskErrors?.fields?.readTimeoutSeconds
    });
    mxEvent.addListener(readTimeoutSecondsField, mxEvent.CHANGE,
        mxUtils.bind(this, function (evt) {
            task.readTimeoutSeconds = evt.target.value;
            updateCellValue(model, cell, {readTimeoutSeconds: task.readTimeoutSeconds});
        }));
    taskTabHttp.appendChild(readTimeoutSecondsField);
};

Sidebar.prototype.createEmailTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();

    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = prepareEmailTaskErrors(taskErrors);

    const taskTabEmail = this.createTab(tabsContainer, 'taskTabEmail', mxResources.get('taskTabEmail'), [], fieldErrors);

    const emailEditor = document.createElement('div');
    emailEditor.id = 'emailEditor';
    emailEditor.className = 'ui active centered inline loader';

    taskTabEmail.appendChild(emailEditor);
};

Sidebar.prototype.createAIChatTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();

    const taskErrors = graph.getTaskErrors(cell.id);

    const messageErrors = [
        ...collectFieldErrors(taskErrors?.fields?.userMessages),
        ...collectFieldErrors(taskErrors?.fields?.systemMessages)
    ];

    const chatModelErrors = collectFieldErrors(taskErrors?.fields?.chatModel);
    const mcpServerErrors = collectFieldErrors(taskErrors?.fields?.mcpServers);

    const createAIChatSectionTab = (id, name, editorId, fieldErrors = []) => {
        const tab = this.createTab(tabsContainer, id, name, [], fieldErrors);
        tab.id = editorId;
        return tab;
    };

    createAIChatSectionTab('taskTabAIChatMessages', mxResources.get('aiChatMessagesTab'), 'aiChatMessagesEditor', messageErrors);
    createAIChatSectionTab('taskTabAIChatModel', mxResources.get('aiChatModelTab'), 'aiChatModelEditor', chatModelErrors);
    createAIChatSectionTab('taskTabAIChatMCPServers', mxResources.get('aiChatMCPServersTab'), 'aiChatMCPServersEditor', mcpServerErrors);
};

Sidebar.prototype.createAIVectorStoreTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const taskErrors = graph.getTaskErrors(cell.id);
    const tab = this.createTab(tabsContainer, 'taskTabAIVectorStore', mxResources.get('taskTabAIVectorStore'), [],
        ['vectorStore', 'documentsExpression', 'queryExpression', 'idsExpression', 'topK',
            'similarityThreshold'].flatMap(field => collectFieldErrors(taskErrors?.fields?.[field])));
    const form = createAIVectorStoreTaskForm({
        task: deepCopy(cell.getValue()), resources: model.getRoot().getValue().resources,
        taskErrors, editable: this.editorUi.editor.editable, mxResources,
        onChange: value => {
            const next = deepCopy(cell.getValue());
            ['vectorStore', 'documentsExpression', 'queryExpression', 'idsExpression',
                'topK', 'similarityThreshold'].forEach(field => delete next[field]);
            const updated = {...next, ...value};
            model.setStyle(cell, mxUtils.setStyle(model.getStyle(cell), 'image',
                vectorTaskImage(updated, model.getRoot().getValue().resources)));
            model.setValue(cell, updated);
        }
    });
    this.container.aiVectorStoreTaskForm = form;
    tab.appendChild(form.element);
    if (cell.getValue().type === 'AIVectorStore') {
        import(/* webpackChunkName: "vectorStoreInputHelp" */ "./help/vectorStoreInputHelp")
            .then(module => form.appendVectorStoreInputHelp(module.vectorStoreInputHelp))
            .catch(error => logUnableToLoadHelpError(error));
    }
};

Sidebar.prototype.createDocumentReaderTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const taskErrors = graph.getTaskErrors(cell.id);
    const readerFields = ['sourceExpression', 'keysToUse', 'pointer', 'charset', 'selector', 'separator',
        'allElements', 'groupByElement', 'includeLinkUrls', 'metadataTags',
        'horizontalRuleCreateDocument', 'includeCodeBlock', 'includeBlockquote'];
    const tab = this.createTab(tabsContainer, 'taskTabDocumentReader', mxResources.get('taskTabDocumentReader'), [],
        readerFields.flatMap(field => collectFieldErrors(taskErrors?.fields?.[field])));
    const form = createDocumentReaderTaskForm({
        task: deepCopy(cell.getValue()), taskErrors, editable: this.editorUi.editor.editable, mxResources,
        onChange: patch => updateCellValue(model, cell, next => {
            readerFields.forEach(field => delete next[field]);
            return {...next, ...patch};
        })
    });
    this.container.documentReaderTaskForm = form;
    tab.appendChild(form.element);
    loadJsonExpressionHelp(help => form.appendJsonExpressionHelp(help), this.container);
};

Sidebar.prototype.createJsonConverterTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const taskErrors = graph.getTaskErrors(cell.id);
    const tab = this.createTab(tabsContainer, 'taskTabJsonConverter', mxResources.get('taskTabJsonConverter'), [],
        ['sourceExpression', 'sourceType'].flatMap(field => collectFieldErrors(taskErrors?.fields?.[field])));
    const form = createJsonConverterTaskForm({
        task: deepCopy(cell.getValue()), taskErrors, editable: this.editorUi.editor.editable, mxResources,
        onChange: patch => updateCellValue(model, cell, patch)
    });
    this.container.jsonConverterTaskForm = form;
    tab.appendChild(form.element);
    loadJsonExpressionHelp(help => form.appendJsonExpressionHelp(help), this.container);
};

Sidebar.prototype.createTaskJQTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = [];
    taskErrors?.fields?.filter && fieldErrors.push(taskErrors.fields.filter);

    const taskTabJQ = this.createTab(tabsContainer, 'taskTabJQ', mxResources.get('taskTabJQ'), [], fieldErrors);
    const jqTaskForm = createJQTaskForm({
        task,
        taskErrors,
        editable: this.editorUi.editor.editable,
        mxResources,
        onJQChange: patch => updateCellValue(model, cell, patch),
        onTestJQFilter: ({event, task: jqTask, filter}) => {
            const apiElement = event.target.closest?.('button') || document.getElementById('testJQFilter');
            document.getElementById('testJQFilterResponse')?.remove();
            $(apiElement).api('destroy');

            if (isEmpty(filter)) {
                event.preventDefault();
                return;
            }

            $(apiElement).api({
                url: '/api/jq-test',
                method: 'POST',
                data: stringifyJSON({
                    input: jqTask.inputParameters,
                    jqFilter: filter
                }),
                beforeXHR: function (xhr) {
                    xhr.setRequestHeader('Content-Type', 'application/json');
                    return xhr;
                },
                onComplete: function (response, element, xhr) {
                    setTestButtonLoading(apiElement, false);
                    const {status, statusText} = xhr;
                    renderTestResponseField({
                        id: 'testJQFilterResponse',
                        label: mxResources.get('httpResponse', [status, statusText]),
                        error: !(status === 0 || (status >= 200 && status < 400)),
                        response,
                        afterElement: document.getElementById('testJQFilterField')
                    });
                }
            }).api('query');
            setTestButtonLoading(apiElement, true);
        }
    });
    taskTabJQ.appendChild(jqTaskForm.element);
};

Sidebar.prototype.createTaskMapTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const objectErrors = [];
    appendFieldErrors(objectErrors, taskErrors?.fields?.itemProcessor?.startTask);
    appendFieldErrors(objectErrors, taskErrors?.fields?.itemProcessor?.tasks);

    const fieldErrors = [];
    taskErrors?.fields?.itemsExpression && fieldErrors.push(taskErrors.fields.itemsExpression);
    taskErrors?.fields?.maxConcurrency && fieldErrors.push(taskErrors.fields.maxConcurrency);
    taskErrors?.fields?.maxConcurrencyExpression && fieldErrors.push(taskErrors.fields.maxConcurrencyExpression);

    const taskTabMap = this.createTab(tabsContainer, 'taskTabMap', mxResources.get('taskTabMap'), objectErrors, fieldErrors);
    const mapTaskForm = createMapTaskForm({
        task,
        taskErrors,
        editable: this.editorUi.editor.editable,
        mxResources,
        onMapChange: patch => updateCellValue(model, cell, patch)
    });
    taskTabMap.appendChild(mapTaskForm.element);

    loadJsonExpressionHelp(jsonExpressionHelp => mapTaskForm.appendJsonExpressionHelp(jsonExpressionHelp), this.container);
};

Sidebar.prototype.createTaskWaitTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = [];
    taskErrors?.fields?.seconds && fieldErrors.push(taskErrors.fields.seconds);
    taskErrors?.fields?.secondsExpression && fieldErrors.push(taskErrors.fields.secondsExpression);
    taskErrors?.fields?.timestamp && fieldErrors.push(taskErrors.fields.timestamp);
    taskErrors?.fields?.timestampExpression && fieldErrors.push(taskErrors.fields.timestampExpression);

    const tabWait = this.createTab(tabsContainer, 'taskWait', mxResources.get('taskTabWait'), [], fieldErrors);
    const waitTaskForm = createWaitTaskForm({
        task,
        taskErrors,
        editable: this.editorUi.editor.editable,
        mxResources,
        onWaitChange: patch => updateCellValue(model, cell, patch),
        onTimestampCalendarMount: ({element, minDate, minTimeGap, onChange}) => {
            $(element).calendar({
                type: 'datetime',
                minDate,
                minTimeGap,
                onChange: date => onChange(date?.toISOString())
            });
        }
    });
    tabWait.appendChild(waitTaskForm.element);

    loadJsonExpressionHelp(jsonExpressionHelp => waitTaskForm.appendJsonExpressionHelp(jsonExpressionHelp), this.container);
};

Sidebar.prototype.createTaskProcessorTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = [];
    taskErrors?.fields?.processor && fieldErrors.push(taskErrors.fields.processor);

    const tabProcessor = this.createTab(tabsContainer, 'taskProcessor', mxResources.get('taskTabProcessor'), [], fieldErrors);
    tabProcessor.insertAdjacentHTML('beforeend', `<p>${mxResources.get('taskTabProcessorHelp')}</p>`);

    const defaultOption = {
        value: '',
        name: mxResources.get('processorPage')
    };

    const processor = normalizePageReference(task.processor);
    const templateTaskProcessorPage = typeof task.templateSourceTaskName === 'string'
        ? this.currentWorkflowDefinition.templateProcessorPages?.[task.templateSourceTaskName]
        : null;
    const hasTemplateTaskProcessorPage = !isEmpty(templateTaskProcessorPage);
    const pageDropdownField = this.createDropdown({
        label: mxResources.get('page'),
        id: 'processorPage',
        name: 'processorPage',
        value: templateTaskProcessorPage ? templateTaskProcessorPage.title : pageReferencePageId(processor),
        error: taskErrors?.fields?.processor,
        clearable: true,
        searchable: true,
        disabled: hasTemplateTaskProcessorPage,
        options: templateTaskProcessorPage ? [{
            value: templateTaskProcessorPage.title,
            name: templateTaskProcessorPage.title
        }] : [defaultOption]
    });
    const pageVersionDropdownField = this.createDropdown({
        label: mxResources.get('pageVersion'),
        id: 'processorPageVersion',
        name: 'processorPageVersion',
        className: 'processorPageVersion',
        value: templateTaskProcessorPage ? String(templateTaskProcessorPage.pageVersion) : pageReferenceVersion(processor),
        clearable: true,
        searchable: true,
        disabled: hasTemplateTaskProcessorPage || isEmpty(pageReferencePageId(processor)),
        options: templateTaskProcessorPage ? [{
            value: String(templateTaskProcessorPage.pageVersion),
            name: `Version ${templateTaskProcessorPage.pageVersion}`
        }] : [{
            value: '',
            name: mxResources.get('latestPageVersion')
        }]
    });

    tabProcessor.appendChild(pageDropdownField);
    tabProcessor.appendChild(pageVersionDropdownField);

    if (templateTaskProcessorPage) {
        const message = document.createElement('div');
        message.className = 'ui info visible message';
        message.textContent = `This template will copy the published Page ${templateTaskProcessorPage.title} when you first save.`;
        tabProcessor.appendChild(message);
    }

    appendPagePreview(tabProcessor, processor);
};

Sidebar.prototype.createErrorHandlingTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);
    const retryErrors = taskErrors?.fields;

    const fieldErrors = [];
    retryErrors?.intervalSeconds && fieldErrors.push(retryErrors.intervalSeconds);
    retryErrors?.maxAttempts && fieldErrors.push(retryErrors.maxAttempts);
    retryErrors?.backoffRate && fieldErrors.push(retryErrors.backoffRate);

    const taskErrorHandling = this.createTab(tabsContainer, 'taskErrorHandling', mxResources.get('taskErrorHandling'), [], fieldErrors);
    const retryForm = createRetryForm({
        task,
        retryErrors,
        editable: this.editorUi.editor.editable,
        mxResources,
        onRetryChange: patch => updateCellValue(model, cell, patch)
    });
    taskErrorHandling.appendChild(retryForm.element);
};

Sidebar.prototype.createTaskRoutingTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const taskErrors = graph.getTaskErrors(cell.id);
    const hasParallelRetry = (cell.isStart() && cell.isSplit()) || cellSupportsParallelMetadataRetryControls(cell);

    const fieldErrors = [];
    taskErrors?.fields?.takeRoutesOf && fieldErrors.push(taskErrors.fields.takeRoutesOf);
    const transitionTaskValuePath = transitionInputParametersTaskValuePath(cell, graph.getModel());
    const transitionInputParametersError = transitionTaskValuePath
        ? taskErrors?.fields?.[transitionTaskValuePath]?.inputParameters
        : cell.isStart() ? taskErrors?.fields?.inputParameters : null;
    transitionInputParametersError && fieldErrors.push(transitionInputParametersError);
    if (hasParallelRetry) {
        const retryErrors = cell.isStart() ? taskErrors?.fields : taskErrors?.fields?.parallelTask;
        retryErrors?.intervalSeconds && fieldErrors.push(retryErrors.intervalSeconds);
        retryErrors?.maxAttempts && fieldErrors.push(retryErrors.maxAttempts);
        retryErrors?.backoffRate && fieldErrors.push(retryErrors.backoffRate);
    }

    const taskRouting = this.createTab(tabsContainer, 'taskRouting', mxResources.get('taskTabRouting'), [], fieldErrors);

    if (!cell.isStart()) {
        taskRouting.appendChild(this.createTaskIncomingRouting(cell));
    }

    if (!cell.isEnd()) {
        taskRouting.appendChild(this.createTaskOutgoingRouting(cell));
    }

    if (hasParallelRetry) {
        const parallelRetrySection = document.createElement('div');
        parallelRetrySection.className = 'field';
        const retryForm = createRetryForm({
            task: deepCopy(cell.getValue()),
            retryErrors: cell.isStart() ? taskErrors?.fields : taskErrors?.fields?.parallelTask,
            editable: this.editorUi.editor.editable,
            mxResources,
            taskValuePath: cell.isStart() ? null : 'parallelTask',
            showHelp: false,
            onRetryChange: patch => updateCellValue(graph.getModel(), cell, patch)
        });
        parallelRetrySection.appendChild(retryForm.element);
        taskRouting.appendChild(parallelRetrySection);
    }
};

Sidebar.prototype.createTaskIncomingRouting = function (cell) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());

    const routingContainer = document.createElement('div');
    routingContainer.className = 'field';

    routingContainer.insertAdjacentHTML('beforeend', `<h4 class="ui dividing header">${mxResources.get('taskIncomingRouting')}</h4>`);

    const joinType = task.joinType;
    const collectorTaskFields = this.createCheckboxes({
        name: 'joinType',
        value: joinType,
        type: 'checkbox',
        options: [{
            label: mxResources.get('collectorTask'),
            help: mxResources.get('collectorTaskHelp'),
            className: 'inline',
            value: 'And',
            onChange: mxUtils.bind(this, function (evt) {
                task.joinType = evt.target.checked ? 'And' : 'Or';
                updateCellValue(model, cell, {joinType: task.joinType});
                graph.fireEvent(new mxEventObject(UPDATE_CELL_VALUE, 'cell', cell));
            })
        }]
    });

    routingContainer.appendChild(collectorTaskFields);

    return routingContainer
}

Sidebar.prototype.createTaskOutgoingRouting = function (cell) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const routingContainer = document.createElement('div');
    routingContainer.className = 'field';

    routingContainer.insertAdjacentHTML('beforeend', `<h4 class="ui dividing header">${mxResources.get('taskOutgoingRouting')}</h4>`);

    const splitType = task.splitType;
    const takeRoutesOfFields = this.createRadioFields({
        label: mxResources.get('takeRoutesOf'),
        name: 'takeRoutesOf',
        value: splitType,
        error: taskErrors?.fields?.takeRoutesOf,
        className: 'required',
        options: [{
            label: mxResources.get('firstTrueConditions'),
            className: 'inline',
            help: mxResources.get('firstTrueConditionsHelp'),
            value: 'Or',
            onChange: mxUtils.bind(this, function (evt) {
                task.splitType = 'Or';
                updateCellValue(model, cell, {splitType: task.splitType});
                graph.fireEvent(new mxEventObject(UPDATE_CELL_VALUE, 'cell', cell));
            })
        }, {
            label: mxResources.get('allTrueConditions'),
            className: 'inline',
            help: mxResources.get('allTrueConditionsHelp'),
            value: 'And',
            onChange: mxUtils.bind(this, function (evt) {
                task.splitType = 'And';
                updateCellValue(model, cell, {splitType: task.splitType});
                graph.fireEvent(new mxEventObject(UPDATE_CELL_VALUE, 'cell', cell));
            })
        }]
    });

    routingContainer.appendChild(takeRoutesOfFields);

    const outgoingEdges = model.getEdges(cell, false, true, true);
    outgoingEdges.sort((a, b) => a.isConditional() === b.isConditional() ? 0 : a.isConditional() ? -1 : 1);
    if (outgoingEdges.length > 0) {
        const routingList = document.createElement(`${cell.isSplit() ? 'ul' : 'ol'}`);
        routingList.className = 'ui suffixed list';
        outgoingEdges.forEach(edge => {
            const {value: route, target: {value: targetTask}} = edge;
            const routing = document.createElement('li');
            routing.textContent = mxResources.get('taskOutgoingRoute', [route.name || mxResources.get('blankRouteName'), targetTask.name]);
            routingList.appendChild(routing);
        });

        const routingInfo = document.createElement('div');
        routingInfo.className = 'ui message';
        routingInfo.appendChild(routingList);
        routingContainer.appendChild(routingInfo);
    }

    if (cellSupportsRoutingParameters(cell, model)) {
        const inputParametersEditor = document.createElement('div');
        inputParametersEditor.id = 'outgoingRoutingInputParametersEditor';
        inputParametersEditor.className = 'ui active centered inline loader';
        routingContainer.appendChild(inputParametersEditor);
    }

    return routingContainer;
};

Sidebar.prototype.createTaskWorkflowOutput = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = [];
    taskErrors?.fields?.workflowOutput && fieldErrors.push(taskErrors.fields.workflowOutput);

    const taskTabWorkflowOutput = this.createTab(tabsContainer, 'taskTabWorkflowOutput', mxResources.get('taskTabWorkflowOutput'), [], fieldErrors);

    taskTabWorkflowOutput.insertAdjacentHTML('beforeend', `<p>${mxResources.get('workflowOutputHelp')}</p>`);

    const workflowOutputEditor = document.createElement('div');
    workflowOutputEditor.id = 'workflowOutputEditor';
    workflowOutputEditor.className = 'ui active centered inline loader';

    taskTabWorkflowOutput.appendChild(workflowOutputEditor);
};

Sidebar.prototype.createTab = function (tabsContainer, id, name, objectErrors = [], fieldErrors = []) {
    const objectErrorMessages = collectFieldErrors(objectErrors);
    const fieldErrorMessages = collectFieldErrors(fieldErrors);

    const tab = document.createElement('a');
    tab.className = 'item';
    tab.setAttribute('data-tab', id);
    tab.appendChild(document.createTextNode(name));
    tab.id = id;

    const errorCount = objectErrorMessages.length + fieldErrorMessages.length;
    if (errorCount > 0) {
        const errorLabel = document.createElement('span');
        errorLabel.className = 'ui mini red circular label';
        errorLabel.textContent = String(errorCount);
        tab.appendChild(errorLabel);
    }

    tabsContainer.appendChild(tab);

    const container = document.createElement('div');
    container.setAttribute('data-tab', id);
    container.className = 'ui bottom attached tab segment form';
    container.id = `${id}Container`;

    if (!isEmpty(objectErrorMessages)) {
        const errorMessage = document.createElement('div');
        errorMessage.className = 'ui visible message error';
        const errorList = document.createElement('ul');
        errorList.className = 'list';
        objectErrorMessages.forEach(error => {
            const errorItem = document.createElement('li');
            errorItem.textContent = error;
            errorList.appendChild(errorItem);
        });
        errorMessage.appendChild(errorList);
        container.appendChild(errorMessage);
    }

    tabsContainer.parentNode.appendChild(container);

    return container;
};

Sidebar.prototype.createFields = function (id, fieldElements = []) {
    return createFormFields(id, fieldElements);
};

Sidebar.prototype.createField = function ({label, id, className = '', fieldElement, help, error} = {}) {
    return createFormField({label, id, className, fieldElement, help, error});
};

Sidebar.prototype.createInputField = function ({
                                                   label,
                                                   id,
                                                   name,
                                                   className = '',
                                                   type,
                                                   step,
                                                   min,
                                                   value = '',
                                                   placeHolder,
                                                   readOnly,
                                                   disabled,
                                                   help,
                                                   error
                                               } = {}) {
    return createFormInputField({
        label,
        id,
        name,
        className,
        type,
        step,
        min,
        value,
        placeHolder,
        readOnly,
        disabled,
        help,
        error
    });
};

Sidebar.prototype.createTextareaField = function ({
                                                      label,
                                                      id,
                                                      name,
                                                      rows,
                                                      className = '',
                                                      value = '',
                                                      placeHolder,
                                                      error
                                                  } = {}) {
    return createFormTextareaField({
        label,
        id,
        name,
        rows,
        className,
        value,
        placeHolder,
        error
    });
};

Sidebar.prototype.createDropdown = function ({
                                                 label,
                                                 help,
                                                 id = '',
                                                 name = '',
                                                 className = '',
                                                 value = '',
                                                 error,
                                                 multiple = false,
                                                 clearable = false,
                                                 searchable = false,
                                                 disabled = false,
                                                 options = []
                                             } = {}) {
    return createFormDropdown({
        label,
        help,
        id,
        name,
        className,
        value,
        error,
        multiple,
        clearable,
        searchable,
        disabled,
        options
    });
};

Sidebar.prototype.createCheckboxFields = function (args) {
    return this.createCheckboxes({...args, type: 'checkbox'});
};

Sidebar.prototype.createRadioFields = function (args) {
    return this.createCheckboxes({...args, type: 'radio'});
};

Sidebar.prototype.createCheckboxes = function ({label, name, value, type, options, error, className} = {}) {
    return createFormCheckboxes({label, name, value, type, options, error, className});
};

Sidebar.prototype.createTable = function (labels = [], columns = []) {
    return createFormTable(labels, columns);
};
